import type { SeedQuestionItem } from './verifiedQuestions.ts';

export type CanonicalTestament = 'Old Testament' | 'New Testament';

export type CanonicalGenerationCategory =
  | 'Old Testament'
  | 'New Testament'
  | 'Bible Characters'
  | 'Bible Events'
  | 'Bible Geography'
  | 'Bible History'
  | 'Biblical Teachings'
  | 'Biblical Knowledge'
  | 'Advanced Biblical Studies'
  | 'Prophecies and Fulfillments';

export const CANONICAL_GENERATION_CATEGORIES: CanonicalGenerationCategory[] = [
  'Old Testament',
  'New Testament',
  'Bible Characters',
  'Bible Events',
  'Bible Geography',
  'Bible History',
  'Biblical Teachings',
  'Biblical Knowledge',
  'Advanced Biblical Studies',
  'Prophecies and Fulfillments',
];

export type CanonicalDifficulty = 'Easy' | 'Medium' | 'Hard';

export const CANONICAL_DIFFICULTIES: CanonicalDifficulty[] = [
  'Easy',
  'Medium',
  'Hard',
];

export const CANONICAL_DIFFICULTY_LEVELS: CanonicalDifficulty[] = CANONICAL_DIFFICULTIES;

export type SupportedQuestionLanguage = 'en' | 'tl' | 'ceb';

export const SUPPORTED_QUESTION_LANGUAGES: SupportedQuestionLanguage[] = [
  'en',
  'tl',
  'ceb',
];

export function normalizeDifficultyBucket(difficulty: string): CanonicalDifficulty {
  const lower = (difficulty || '').trim().toLowerCase();
  if (lower === 'beginner' || lower === 'easy' || lower === '1' || lower === '2') {
    return 'Easy';
  }
  if (lower === 'advanced' || lower === 'expert' || lower === 'scholar' || lower === 'very hard' || lower === 'hard' || lower === '4' || lower === '5') {
    return 'Hard';
  }
  return 'Medium';
}

export interface BibleBookRegistryEntry {
  bookNumber: number; // 1 to 66
  bookName: string;
  aliases: string[];
  testament: CanonicalTestament;
  chaptersCount: number;
  verseCountsByChapter: number[];
  defaultCategory: CanonicalGenerationCategory;
  localizedNames: {
    en: string;
    tl: string;
    ceb: string;
  };
}

/**
 * COMPLETE 66-BOOK PROTESTANT CANON REGISTRY (Genesis through Revelation)
 * Includes exact chapter counts (929 OT + 260 NT = 1,189 total chapters)
 * and exact verse counts per chapter for deterministic chapter/verse verification.
 */
export const BIBLE_66_BOOK_REGISTRY: BibleBookRegistryEntry[] = [
  // ======================== OLD TESTAMENT (39 BOOKS) ========================
  {
    bookNumber: 1,
    bookName: 'Genesis',
    aliases: ['gen', 'ge'],
    testament: 'Old Testament',
    chaptersCount: 50,
    verseCountsByChapter: [
      31, 25, 24, 26, 32, 22, 24, 22, 29, 32,
      32, 20, 18, 24, 21, 16, 27, 33, 38, 18,
      34, 24, 20, 67, 34, 35, 46, 22, 35, 43,
      55, 32, 20, 31, 29, 43, 36, 30, 23, 23,
      57, 38, 34, 34, 28, 34, 31, 22, 33, 26,
    ],
    defaultCategory: 'Old Testament',
    localizedNames: { en: 'Genesis', tl: 'Genesis', ceb: 'Genesis' },
  },
  {
    bookNumber: 2,
    bookName: 'Exodus',
    aliases: ['exo', 'ex', 'exod'],
    testament: 'Old Testament',
    chaptersCount: 40,
    verseCountsByChapter: [
      22, 25, 22, 31, 23, 30, 25, 32, 35, 29,
      10, 51, 22, 31, 27, 36, 16, 27, 25, 26,
      36, 31, 33, 18, 40, 37, 21, 43, 46, 38,
      18, 35, 23, 35, 35, 38, 29, 31, 43, 38,
    ],
    defaultCategory: 'Bible Events',
    localizedNames: { en: 'Exodus', tl: 'Exodo', ceb: 'Exodo' },
  },
  {
    bookNumber: 3,
    bookName: 'Leviticus',
    aliases: ['lev', 'le', 'lv'],
    testament: 'Old Testament',
    chaptersCount: 27,
    verseCountsByChapter: [
      17, 16, 17, 35, 19, 30, 38, 36, 24, 20,
      47, 8, 59, 57, 33, 34, 16, 30, 37, 27,
      24, 33, 44, 23, 55, 46, 34,
    ],
    defaultCategory: 'Biblical Teachings',
    localizedNames: { en: 'Leviticus', tl: 'Levitico', ceb: 'Levitico' },
  },
  {
    bookNumber: 4,
    bookName: 'Numbers',
    aliases: ['num', 'nu', 'nm'],
    testament: 'Old Testament',
    chaptersCount: 36,
    verseCountsByChapter: [
      54, 34, 51, 49, 31, 27, 89, 26, 23, 36,
      35, 16, 33, 45, 41, 50, 13, 32, 22, 29,
      35, 41, 30, 25, 18, 65, 23, 31, 40, 16,
      54, 42, 56, 29, 34, 13,
    ],
    defaultCategory: 'Bible Geography',
    localizedNames: { en: 'Numbers', tl: 'Mga Bilang', ceb: 'Numeros' },
  },
  {
    bookNumber: 5,
    bookName: 'Deuteronomy',
    aliases: ['deut', 'deu', 'dt'],
    testament: 'Old Testament',
    chaptersCount: 34,
    verseCountsByChapter: [
      46, 37, 29, 49, 33, 25, 26, 20, 29, 22,
      32, 32, 18, 29, 23, 22, 20, 22, 21, 20,
      23, 30, 25, 22, 19, 19, 26, 68, 29, 20,
      30, 52, 29, 12,
    ],
    defaultCategory: 'Biblical Teachings',
    localizedNames: { en: 'Deuteronomy', tl: 'Deuteronomio', ceb: 'Deuteronomio' },
  },
  {
    bookNumber: 6,
    bookName: 'Joshua',
    aliases: ['josh', 'jos'],
    testament: 'Old Testament',
    chaptersCount: 24,
    verseCountsByChapter: [
      18, 24, 17, 24, 15, 27, 26, 35, 27, 43,
      23, 24, 33, 15, 63, 10, 18, 28, 51, 9,
      45, 34, 16, 33,
    ],
    defaultCategory: 'Bible History',
    localizedNames: { en: 'Joshua', tl: 'Josue', ceb: 'Josue' },
  },
  {
    bookNumber: 7,
    bookName: 'Judges',
    aliases: ['judg', 'jdg', 'jg'],
    testament: 'Old Testament',
    chaptersCount: 21,
    verseCountsByChapter: [
      36, 23, 31, 24, 31, 40, 25, 35, 57, 18,
      40, 15, 25, 20, 20, 31, 13, 31, 30, 48,
      25,
    ],
    defaultCategory: 'Bible Characters',
    localizedNames: { en: 'Judges', tl: 'Mga Hukom', ceb: 'Mga Maghuhukom' },
  },
  {
    bookNumber: 8,
    bookName: 'Ruth',
    aliases: ['rut', 'rth'],
    testament: 'Old Testament',
    chaptersCount: 4,
    verseCountsByChapter: [22, 23, 18, 22],
    defaultCategory: 'Bible Characters',
    localizedNames: { en: 'Ruth', tl: 'Ruth', ceb: 'Ruth' },
  },
  {
    bookNumber: 9,
    bookName: '1 Samuel',
    aliases: ['1 sam', '1sam', 'i samuel', 'first samuel'],
    testament: 'Old Testament',
    chaptersCount: 31,
    verseCountsByChapter: [
      28, 36, 21, 22, 12, 21, 17, 22, 27, 27,
      15, 25, 23, 52, 35, 23, 58, 30, 24, 42,
      15, 23, 29, 22, 44, 25, 12, 25, 11, 31,
      13,
    ],
    defaultCategory: 'Bible History',
    localizedNames: { en: '1 Samuel', tl: '1 Samuel', ceb: '1 Samuel' },
  },
  {
    bookNumber: 10,
    bookName: '2 Samuel',
    aliases: ['2 sam', '2sam', 'ii samuel', 'second samuel'],
    testament: 'Old Testament',
    chaptersCount: 24,
    verseCountsByChapter: [
      27, 32, 39, 12, 25, 23, 29, 18, 13, 19,
      27, 31, 39, 33, 37, 23, 29, 33, 43, 26,
      22, 51, 39, 25,
    ],
    defaultCategory: 'Bible History',
    localizedNames: { en: '2 Samuel', tl: '2 Samuel', ceb: '2 Samuel' },
  },
  {
    bookNumber: 11,
    bookName: '1 Kings',
    aliases: ['1 kgs', '1kgs', 'i kings', 'first kings'],
    testament: 'Old Testament',
    chaptersCount: 22,
    verseCountsByChapter: [
      53, 46, 28, 34, 18, 38, 51, 66, 28, 29,
      43, 33, 34, 31, 34, 34, 24, 46, 21, 43,
      29, 53,
    ],
    defaultCategory: 'Bible History',
    localizedNames: { en: '1 Kings', tl: '1 Mga Hari', ceb: '1 Mga Hari' },
  },
  {
    bookNumber: 12,
    bookName: '2 Kings',
    aliases: ['2 kgs', '2kgs', 'ii kings', 'second kings'],
    testament: 'Old Testament',
    chaptersCount: 25,
    verseCountsByChapter: [
      18, 25, 27, 44, 27, 33, 20, 29, 37, 36,
      21, 21, 25, 29, 38, 20, 41, 37, 37, 21,
      26, 20, 37, 20, 30,
    ],
    defaultCategory: 'Bible History',
    localizedNames: { en: '2 Kings', tl: '2 Mga Hari', ceb: '2 Mga Hari' },
  },
  {
    bookNumber: 13,
    bookName: '1 Chronicles',
    aliases: ['1 chr', '1chr', 'i chronicles', 'first chronicles'],
    testament: 'Old Testament',
    chaptersCount: 29,
    verseCountsByChapter: [
      54, 55, 24, 43, 26, 81, 40, 40, 44, 14,
      47, 40, 14, 17, 29, 43, 27, 17, 19, 8,
      30, 19, 32, 31, 31, 32, 34, 21, 30,
    ],
    defaultCategory: 'Bible History',
    localizedNames: { en: '1 Chronicles', tl: '1 Mga Cronica', ceb: '1 Mga Cronicas' },
  },
  {
    bookNumber: 14,
    bookName: '2 Chronicles',
    aliases: ['2 chr', '2chr', 'ii chronicles', 'second chronicles'],
    testament: 'Old Testament',
    chaptersCount: 36,
    verseCountsByChapter: [
      17, 18, 17, 22, 14, 42, 22, 18, 31, 19,
      23, 16, 22, 15, 19, 14, 19, 34, 11, 37,
      20, 12, 21, 27, 28, 23, 9, 27, 36, 27,
      21, 33, 25, 33, 27, 23,
    ],
    defaultCategory: 'Bible History',
    localizedNames: { en: '2 Chronicles', tl: '2 Mga Cronica', ceb: '2 Mga Cronicas' },
  },
  {
    bookNumber: 15,
    bookName: 'Ezra',
    aliases: ['ezr'],
    testament: 'Old Testament',
    chaptersCount: 10,
    verseCountsByChapter: [11, 70, 13, 24, 17, 22, 28, 36, 15, 44],
    defaultCategory: 'Bible History',
    localizedNames: { en: 'Ezra', tl: 'Ezra', ceb: 'Esdras' },
  },
  {
    bookNumber: 16,
    bookName: 'Nehemiah',
    aliases: ['neh', 'ne'],
    testament: 'Old Testament',
    chaptersCount: 13,
    verseCountsByChapter: [11, 20, 32, 23, 19, 19, 73, 18, 38, 39, 36, 47, 31],
    defaultCategory: 'Bible History',
    localizedNames: { en: 'Nehemiah', tl: 'Nehemias', ceb: 'Nehemias' },
  },
  {
    bookNumber: 17,
    bookName: 'Esther',
    aliases: ['est', 'esth'],
    testament: 'Old Testament',
    chaptersCount: 10,
    verseCountsByChapter: [22, 23, 15, 17, 14, 14, 10, 17, 32, 3],
    defaultCategory: 'Bible Characters',
    localizedNames: { en: 'Esther', tl: 'Ester', ceb: 'Ester' },
  },
  {
    bookNumber: 18,
    bookName: 'Job',
    aliases: ['jb'],
    testament: 'Old Testament',
    chaptersCount: 42,
    verseCountsByChapter: [
      22, 13, 26, 21, 27, 30, 21, 22, 35, 22,
      20, 25, 28, 22, 35, 22, 16, 21, 29, 29,
      34, 30, 17, 25, 6, 14, 23, 28, 25, 31,
      40, 22, 33, 37, 16, 33, 24, 41, 30, 24,
      34, 17,
    ],
    defaultCategory: 'Biblical Teachings',
    localizedNames: { en: 'Job', tl: 'Job', ceb: 'Job' },
  },
  {
    bookNumber: 19,
    bookName: 'Psalms',
    aliases: ['psalm', 'ps', 'psa', 'psm'],
    testament: 'Old Testament',
    chaptersCount: 150,
    verseCountsByChapter: [
      6, 12, 8, 8, 12, 10, 17, 9, 20, 18,
      7, 8, 6, 7, 5, 11, 15, 50, 14, 9,
      13, 31, 6, 10, 22, 12, 14, 9, 11, 12,
      24, 11, 22, 22, 28, 12, 40, 22, 13, 17,
      13, 11, 5, 26, 17, 11, 9, 14, 20, 23,
      19, 9, 6, 7, 23, 13, 11, 11, 17, 12,
      8, 12, 11, 10, 13, 20, 7, 35, 36, 5,
      24, 20, 28, 23, 10, 12, 20, 72, 13, 19,
      16, 8, 18, 12, 13, 17, 7, 18, 52, 17,
      16, 15, 5, 23, 11, 13, 12, 9, 9, 5,
      8, 28, 22, 35, 45, 48, 43, 13, 31, 7,
      10, 10, 9, 8, 18, 19, 2, 29, 176, 7,
      8, 9, 4, 8, 5, 6, 5, 6, 8, 8,
      3, 18, 3, 3, 21, 26, 9, 8, 24, 13,
      10, 7, 12, 15, 21, 10, 20, 14, 9, 6,
    ],
    defaultCategory: 'Biblical Teachings',
    localizedNames: { en: 'Psalms', tl: 'Mga Awit', ceb: 'Mga Salmo' },
  },
  {
    bookNumber: 20,
    bookName: 'Proverbs',
    aliases: ['prov', 'pro', 'prv'],
    testament: 'Old Testament',
    chaptersCount: 31,
    verseCountsByChapter: [
      33, 22, 35, 27, 23, 35, 27, 36, 18, 32,
      31, 28, 25, 35, 33, 33, 28, 24, 29, 30,
      31, 29, 35, 34, 28, 28, 27, 28, 27, 33,
      31,
    ],
    defaultCategory: 'Biblical Teachings',
    localizedNames: { en: 'Proverbs', tl: 'Mga Kawikaan', ceb: 'Mga Panultihon' },
  },
  {
    bookNumber: 21,
    bookName: 'Ecclesiastes',
    aliases: ['eccl', 'ecc', 'qoheleth'],
    testament: 'Old Testament',
    chaptersCount: 12,
    verseCountsByChapter: [18, 26, 22, 16, 20, 12, 29, 17, 18, 20, 10, 14],
    defaultCategory: 'Biblical Teachings',
    localizedNames: { en: 'Ecclesiastes', tl: 'Ang Mangangaral', ceb: 'Ecclesiastes' },
  },
  {
    bookNumber: 22,
    bookName: 'Song of Solomon',
    aliases: ['song of songs', 'song', 'sos', 'canticles'],
    testament: 'Old Testament',
    chaptersCount: 8,
    verseCountsByChapter: [17, 17, 11, 16, 16, 13, 13, 14],
    defaultCategory: 'Biblical Knowledge',
    localizedNames: { en: 'Song of Solomon', tl: 'Awit ni Solomon', ceb: 'Awit ni Solomon' },
  },
  {
    bookNumber: 23,
    bookName: 'Isaiah',
    aliases: ['isa', 'is'],
    testament: 'Old Testament',
    chaptersCount: 66,
    verseCountsByChapter: [
      31, 22, 26, 6, 30, 13, 25, 22, 21, 34,
      16, 6, 22, 32, 9, 14, 14, 7, 25, 6,
      17, 25, 18, 23, 12, 21, 13, 29, 24, 33,
      9, 20, 24, 17, 10, 22, 38, 22, 8, 31,
      29, 25, 28, 28, 25, 13, 15, 22, 26, 11,
      23, 15, 12, 17, 13, 12, 21, 14, 21, 22,
      11, 12, 19, 12, 25, 24,
    ],
    defaultCategory: 'Prophecies and Fulfillments',
    localizedNames: { en: 'Isaiah', tl: 'Isaias', ceb: 'Isaias' },
  },
  {
    bookNumber: 24,
    bookName: 'Jeremiah',
    aliases: ['jer', 'je'],
    testament: 'Old Testament',
    chaptersCount: 52,
    verseCountsByChapter: [
      19, 37, 25, 31, 31, 30, 34, 22, 26, 25,
      23, 17, 27, 22, 21, 21, 27, 23, 15, 18,
      14, 30, 40, 10, 38, 24, 22, 17, 32, 24,
      40, 44, 26, 22, 19, 32, 21, 28, 18, 16,
      18, 22, 13, 30, 5, 28, 7, 47, 39, 46,
      64, 34,
    ],
    defaultCategory: 'Prophecies and Fulfillments',
    localizedNames: { en: 'Jeremiah', tl: 'Jeremias', ceb: 'Jeremias' },
  },
  {
    bookNumber: 25,
    bookName: 'Lamentations',
    aliases: ['lam', 'la'],
    testament: 'Old Testament',
    chaptersCount: 5,
    verseCountsByChapter: [22, 22, 66, 22, 22],
    defaultCategory: 'Old Testament',
    localizedNames: { en: 'Lamentations', tl: 'Mga Panaghoy', ceb: 'Mga Pagbakho' },
  },
  {
    bookNumber: 26,
    bookName: 'Ezekiel',
    aliases: ['ezek', 'eze', 'ezk'],
    testament: 'Old Testament',
    chaptersCount: 48,
    verseCountsByChapter: [
      28, 10, 27, 17, 17, 14, 27, 18, 11, 22,
      25, 28, 23, 23, 8, 63, 24, 32, 14, 49,
      32, 31, 49, 27, 17, 21, 36, 26, 21, 26,
      18, 32, 33, 31, 15, 38, 28, 23, 29, 49,
      26, 20, 27, 31, 25, 24, 23, 35,
    ],
    defaultCategory: 'Prophecies and Fulfillments',
    localizedNames: { en: 'Ezekiel', tl: 'Ezekiel', ceb: 'Ezequiel' },
  },
  {
    bookNumber: 27,
    bookName: 'Daniel',
    aliases: ['dan', 'da', 'dn'],
    testament: 'Old Testament',
    chaptersCount: 12,
    verseCountsByChapter: [21, 49, 30, 37, 31, 28, 28, 27, 27, 21, 45, 13],
    defaultCategory: 'Prophecies and Fulfillments',
    localizedNames: { en: 'Daniel', tl: 'Daniel', ceb: 'Daniel' },
  },
  {
    bookNumber: 28,
    bookName: 'Hosea',
    aliases: ['hos', 'ho'],
    testament: 'Old Testament',
    chaptersCount: 14,
    verseCountsByChapter: [11, 23, 5, 19, 15, 11, 16, 14, 17, 15, 12, 14, 16, 9],
    defaultCategory: 'Prophecies and Fulfillments',
    localizedNames: { en: 'Hosea', tl: 'Oseas', ceb: 'Oseas' },
  },
  {
    bookNumber: 29,
    bookName: 'Joel',
    aliases: ['joe', 'jl'],
    testament: 'Old Testament',
    chaptersCount: 3,
    verseCountsByChapter: [20, 32, 21],
    defaultCategory: 'Prophecies and Fulfillments',
    localizedNames: { en: 'Joel', tl: 'Joel', ceb: 'Joel' },
  },
  {
    bookNumber: 30,
    bookName: 'Amos',
    aliases: ['amo', 'am'],
    testament: 'Old Testament',
    chaptersCount: 9,
    verseCountsByChapter: [15, 16, 15, 13, 27, 14, 17, 14, 15],
    defaultCategory: 'Prophecies and Fulfillments',
    localizedNames: { en: 'Amos', tl: 'Amos', ceb: 'Amos' },
  },
  {
    bookNumber: 31,
    bookName: 'Obadiah',
    aliases: ['obad', 'oba', 'ob'],
    testament: 'Old Testament',
    chaptersCount: 1,
    verseCountsByChapter: [21],
    defaultCategory: 'Prophecies and Fulfillments',
    localizedNames: { en: 'Obadiah', tl: 'Obadias', ceb: ' Abdias' },
  },
  {
    bookNumber: 32,
    bookName: 'Jonah',
    aliases: ['jon', 'jnh'],
    testament: 'Old Testament',
    chaptersCount: 4,
    verseCountsByChapter: [17, 10, 10, 11],
    defaultCategory: 'Bible Events',
    localizedNames: { en: 'Jonah', tl: 'Jonas', ceb: 'Jonas' },
  },
  {
    bookNumber: 33,
    bookName: 'Micah',
    aliases: ['mic', 'mc'],
    testament: 'Old Testament',
    chaptersCount: 7,
    verseCountsByChapter: [16, 13, 12, 13, 15, 16, 20],
    defaultCategory: 'Prophecies and Fulfillments',
    localizedNames: { en: 'Micah', tl: 'Mikas', ceb: 'Miqueas' },
  },
  {
    bookNumber: 34,
    bookName: 'Nahum',
    aliases: ['nah', 'na'],
    testament: 'Old Testament',
    chaptersCount: 3,
    verseCountsByChapter: [15, 13, 19],
    defaultCategory: 'Prophecies and Fulfillments',
    localizedNames: { en: 'Nahum', tl: 'Nahum', ceb: 'Nahum' },
  },
  {
    bookNumber: 35,
    bookName: 'Habakkuk',
    aliases: ['hab', 'hb'],
    testament: 'Old Testament',
    chaptersCount: 3,
    verseCountsByChapter: [17, 20, 19],
    defaultCategory: 'Prophecies and Fulfillments',
    localizedNames: { en: 'Habakkuk', tl: 'Habacuc', ceb: 'Habacuc' },
  },
  {
    bookNumber: 36,
    bookName: 'Zephaniah',
    aliases: ['zeph', 'zep', 'zp'],
    testament: 'Old Testament',
    chaptersCount: 3,
    verseCountsByChapter: [18, 15, 20],
    defaultCategory: 'Prophecies and Fulfillments',
    localizedNames: { en: 'Zephaniah', tl: 'Zefanias', ceb: 'Sofonias' },
  },
  {
    bookNumber: 37,
    bookName: 'Haggai',
    aliases: ['hag', 'hg'],
    testament: 'Old Testament',
    chaptersCount: 2,
    verseCountsByChapter: [15, 23],
    defaultCategory: 'Bible History',
    localizedNames: { en: 'Haggai', tl: 'Hagai', ceb: 'Hageo' },
  },
  {
    bookNumber: 38,
    bookName: 'Zechariah',
    aliases: ['zech', 'zec', 'zc'],
    testament: 'Old Testament',
    chaptersCount: 14,
    verseCountsByChapter: [21, 13, 10, 14, 11, 15, 14, 23, 17, 12, 17, 14, 9, 21],
    defaultCategory: 'Prophecies and Fulfillments',
    localizedNames: { en: 'Zechariah', tl: 'Zacarias', ceb: 'Zacarias' },
  },
  {
    bookNumber: 39,
    bookName: 'Malachi',
    aliases: ['mal', 'ml'],
    testament: 'Old Testament',
    chaptersCount: 4,
    verseCountsByChapter: [14, 17, 18, 6],
    defaultCategory: 'Prophecies and Fulfillments',
    localizedNames: { en: 'Malachi', tl: 'Malakias', ceb: 'Malaquias' },
  },

  // ======================== NEW TESTAMENT (27 BOOKS) ========================
  {
    bookNumber: 40,
    bookName: 'Matthew',
    aliases: ['matt', 'mat', 'mt'],
    testament: 'New Testament',
    chaptersCount: 28,
    verseCountsByChapter: [
      25, 23, 17, 25, 48, 34, 29, 34, 38, 42,
      30, 50, 58, 36, 39, 28, 27, 35, 30, 34,
      46, 46, 39, 51, 46, 75, 66, 20,
    ],
    defaultCategory: 'New Testament',
    localizedNames: { en: 'Matthew', tl: 'Mateo', ceb: 'Mateo' },
  },
  {
    bookNumber: 41,
    bookName: 'Mark',
    aliases: ['mrk', 'mk'],
    testament: 'New Testament',
    chaptersCount: 16,
    verseCountsByChapter: [
      45, 28, 35, 41, 43, 56, 37, 38, 50, 52,
      33, 44, 37, 72, 47, 20,
    ],
    defaultCategory: 'New Testament',
    localizedNames: { en: 'Mark', tl: 'Marcos', ceb: 'Marcos' },
  },
  {
    bookNumber: 42,
    bookName: 'Luke',
    aliases: ['luk', 'lk'],
    testament: 'New Testament',
    chaptersCount: 24,
    verseCountsByChapter: [
      80, 52, 38, 44, 39, 49, 50, 56, 62, 42,
      54, 59, 35, 35, 32, 31, 37, 43, 48, 47,
      38, 71, 56, 53,
    ],
    defaultCategory: 'New Testament',
    localizedNames: { en: 'Luke', tl: 'Lucas', ceb: 'Lucas' },
  },
  {
    bookNumber: 43,
    bookName: 'John',
    aliases: ['jhn', 'jn'],
    testament: 'New Testament',
    chaptersCount: 21,
    verseCountsByChapter: [
      51, 25, 36, 54, 47, 71, 53, 59, 41, 42,
      57, 50, 38, 31, 27, 33, 26, 40, 42, 31,
      25,
    ],
    defaultCategory: 'New Testament',
    localizedNames: { en: 'John', tl: 'Juan', ceb: 'Juan' },
  },
  {
    bookNumber: 44,
    bookName: 'Acts',
    aliases: ['act', 'acts of the apostles'],
    testament: 'New Testament',
    chaptersCount: 28,
    verseCountsByChapter: [
      26, 47, 26, 37, 42, 15, 60, 40, 43, 48,
      30, 25, 52, 28, 41, 40, 34, 28, 41, 38,
      40, 30, 35, 27, 27, 32, 44, 31,
    ],
    defaultCategory: 'Bible History',
    localizedNames: { en: 'Acts', tl: 'Mga Gawa', ceb: 'Mga Buhat' },
  },
  {
    bookNumber: 45,
    bookName: 'Romans',
    aliases: ['rom', 'ro', 'rm'],
    testament: 'New Testament',
    chaptersCount: 16,
    verseCountsByChapter: [
      32, 29, 31, 25, 21, 23, 25, 39, 33, 21,
      36, 21, 14, 23, 33, 27,
    ],
    defaultCategory: 'Biblical Teachings',
    localizedNames: { en: 'Romans', tl: 'Mga Taga-Roma', ceb: 'Mga Taga-Roma' },
  },
  {
    bookNumber: 46,
    bookName: '1 Corinthians',
    aliases: ['1 cor', '1cor', 'i corinthians', 'first corinthians'],
    testament: 'New Testament',
    chaptersCount: 16,
    verseCountsByChapter: [
      31, 16, 23, 21, 13, 20, 40, 13, 27, 33,
      34, 31, 13, 40, 58, 24,
    ],
    defaultCategory: 'Biblical Teachings',
    localizedNames: { en: '1 Corinthians', tl: '1 Mga Taga-Corinto', ceb: '1 Mga Taga-Corinto' },
  },
  {
    bookNumber: 47,
    bookName: '2 Corinthians',
    aliases: ['2 cor', '2cor', 'ii corinthians', 'second corinthians'],
    testament: 'New Testament',
    chaptersCount: 13,
    verseCountsByChapter: [24, 17, 18, 18, 21, 18, 16, 24, 15, 18, 33, 21, 14],
    defaultCategory: 'Biblical Teachings',
    localizedNames: { en: '2 Corinthians', tl: '2 Mga Taga-Corinto', ceb: '2 Mga Taga-Corinto' },
  },
  {
    bookNumber: 48,
    bookName: 'Galatians',
    aliases: ['gal', 'ga'],
    testament: 'New Testament',
    chaptersCount: 6,
    verseCountsByChapter: [24, 21, 29, 31, 26, 18],
    defaultCategory: 'Biblical Teachings',
    localizedNames: { en: 'Galatians', tl: 'Mga Taga-Galacia', ceb: 'Mga Taga-Galacia' },
  },
  {
    bookNumber: 49,
    bookName: 'Ephesians',
    aliases: ['eph', 'ep'],
    testament: 'New Testament',
    chaptersCount: 6,
    verseCountsByChapter: [23, 22, 21, 32, 33, 24],
    defaultCategory: 'Biblical Teachings',
    localizedNames: { en: 'Ephesians', tl: 'Mga Taga-Efeso', ceb: 'Mga Taga-Efeso' },
  },
  {
    bookNumber: 50,
    bookName: 'Philippians',
    aliases: ['phil', 'php', 'pp'],
    testament: 'New Testament',
    chaptersCount: 4,
    verseCountsByChapter: [30, 30, 21, 23],
    defaultCategory: 'Biblical Teachings',
    localizedNames: { en: 'Philippians', tl: 'Mga Taga-Filipos', ceb: 'Mga Taga-Filipos' },
  },
  {
    bookNumber: 51,
    bookName: 'Colossians',
    aliases: ['col', 'co'],
    testament: 'New Testament',
    chaptersCount: 4,
    verseCountsByChapter: [29, 23, 25, 18],
    defaultCategory: 'Biblical Teachings',
    localizedNames: { en: 'Colossians', tl: 'Mga Taga-Colosas', ceb: 'Mga Taga-Colosas' },
  },
  {
    bookNumber: 52,
    bookName: '1 Thessalonians',
    aliases: ['1 thess', '1thess', '1 th', 'i thessalonians', 'first thessalonians'],
    testament: 'New Testament',
    chaptersCount: 5,
    verseCountsByChapter: [10, 20, 13, 18, 28],
    defaultCategory: 'Biblical Teachings',
    localizedNames: { en: '1 Thessalonians', tl: '1 Mga Taga-Tesalonica', ceb: '1 Mga Taga-Tesalonica' },
  },
  {
    bookNumber: 53,
    bookName: '2 Thessalonians',
    aliases: ['2 thess', '2thess', '2 th', 'ii thessalonians', 'second thessalonians'],
    testament: 'New Testament',
    chaptersCount: 3,
    verseCountsByChapter: [12, 17, 18],
    defaultCategory: 'Prophecies and Fulfillments',
    localizedNames: { en: '2 Thessalonians', tl: '2 Mga Taga-Tesalonica', ceb: '2 Mga Taga-Tesalonica' },
  },
  {
    bookNumber: 54,
    bookName: '1 Timothy',
    aliases: ['1 tim', '1tim', '1 ti', 'i timothy', 'first timothy'],
    testament: 'New Testament',
    chaptersCount: 6,
    verseCountsByChapter: [20, 15, 16, 16, 25, 21],
    defaultCategory: 'Biblical Teachings',
    localizedNames: { en: '1 Timothy', tl: '1 Timoteo', ceb: '1 Timoteo' },
  },
  {
    bookNumber: 55,
    bookName: '2 Timothy',
    aliases: ['2 tim', '2tim', '2 ti', 'ii timothy', 'second timothy'],
    testament: 'New Testament',
    chaptersCount: 4,
    verseCountsByChapter: [18, 26, 17, 22],
    defaultCategory: 'Biblical Teachings',
    localizedNames: { en: '2 Timothy', tl: '2 Timoteo', ceb: '2 Timoteo' },
  },
  {
    bookNumber: 56,
    bookName: 'Titus',
    aliases: ['tit', 'ti'],
    testament: 'New Testament',
    chaptersCount: 3,
    verseCountsByChapter: [16, 15, 15],
    defaultCategory: 'Biblical Teachings',
    localizedNames: { en: 'Titus', tl: 'Tito', ceb: 'Tito' },
  },
  {
    bookNumber: 57,
    bookName: 'Philemon',
    aliases: ['phlm', 'phm', 'pm'],
    testament: 'New Testament',
    chaptersCount: 1,
    verseCountsByChapter: [25],
    defaultCategory: 'Bible Characters',
    localizedNames: { en: 'Philemon', tl: 'Filemon', ceb: 'Filemon' },
  },
  {
    bookNumber: 58,
    bookName: 'Hebrews',
    aliases: ['heb', 'he'],
    testament: 'New Testament',
    chaptersCount: 13,
    verseCountsByChapter: [14, 18, 19, 16, 14, 20, 28, 13, 28, 39, 40, 29, 25],
    defaultCategory: 'Advanced Biblical Studies',
    localizedNames: { en: 'Hebrews', tl: 'Mga Hebreo', ceb: 'Mga Hebreohanon' },
  },
  {
    bookNumber: 59,
    bookName: 'James',
    aliases: ['jas', 'jm'],
    testament: 'New Testament',
    chaptersCount: 5,
    verseCountsByChapter: [27, 26, 18, 17, 20],
    defaultCategory: 'Biblical Teachings',
    localizedNames: { en: 'James', tl: 'Santiago', ceb: 'Santiago' },
  },
  {
    bookNumber: 60,
    bookName: '1 Peter',
    aliases: ['1 pet', '1pet', '1 pe', 'i peter', 'first peter'],
    testament: 'New Testament',
    chaptersCount: 5,
    verseCountsByChapter: [25, 25, 22, 19, 14],
    defaultCategory: 'Biblical Teachings',
    localizedNames: { en: '1 Peter', tl: '1 Pedro', ceb: '1 Pedro' },
  },
  {
    bookNumber: 61,
    bookName: '2 Peter',
    aliases: ['2 pet', '2pet', '2 pe', 'ii peter', 'second peter'],
    testament: 'New Testament',
    chaptersCount: 3,
    verseCountsByChapter: [21, 22, 18],
    defaultCategory: 'Biblical Teachings',
    localizedNames: { en: '2 Peter', tl: '2 Pedro', ceb: '2 Pedro' },
  },
  {
    bookNumber: 62,
    bookName: '1 John',
    aliases: ['1 jn', '1jn', 'i john', 'first john'],
    testament: 'New Testament',
    chaptersCount: 5,
    verseCountsByChapter: [10, 29, 24, 21, 21],
    defaultCategory: 'Biblical Teachings',
    localizedNames: { en: '1 John', tl: '1 Juan', ceb: '1 Juan' },
  },
  {
    bookNumber: 63,
    bookName: '2 John',
    aliases: ['2 jn', '2jn', 'ii john', 'second john'],
    testament: 'New Testament',
    chaptersCount: 1,
    verseCountsByChapter: [13],
    defaultCategory: 'Biblical Teachings',
    localizedNames: { en: '2 John', tl: '2 Juan', ceb: '2 Juan' },
  },
  {
    bookNumber: 64,
    bookName: '3 John',
    aliases: ['3 jn', '3jn', 'iii john', 'third john'],
    testament: 'New Testament',
    chaptersCount: 1,
    verseCountsByChapter: [14],
    defaultCategory: 'Bible Characters',
    localizedNames: { en: '3 John', tl: '3 Juan', ceb: '3 Juan' },
  },
  {
    bookNumber: 65,
    bookName: 'Jude',
    aliases: ['jud', 'jd'],
    testament: 'New Testament',
    chaptersCount: 1,
    verseCountsByChapter: [25],
    defaultCategory: 'Advanced Biblical Studies',
    localizedNames: { en: 'Jude', tl: 'Judas', ceb: 'Judas' },
  },
  {
    bookNumber: 66,
    bookName: 'Revelation',
    aliases: ['rev', 're', 'apocalypse', 'revelations'],
    testament: 'New Testament',
    chaptersCount: 22,
    verseCountsByChapter: [
      20, 29, 22, 11, 14, 17, 17, 13, 21, 11,
      19, 17, 18, 20, 8, 21, 18, 24, 21, 15,
      27, 21,
    ],
    defaultCategory: 'Prophecies and Fulfillments',
    localizedNames: { en: 'Revelation', tl: 'Pahayag', ceb: 'Gipadayag' },
  },
];

// Fast lookup map for book resolution
const BOOK_LOOKUP_MAP = new Map<string, BibleBookRegistryEntry>();
for (const entry of BIBLE_66_BOOK_REGISTRY) {
  BOOK_LOOKUP_MAP.set(entry.bookName.toLowerCase(), entry);
  BOOK_LOOKUP_MAP.set(entry.localizedNames.tl.trim().toLowerCase(), entry);
  BOOK_LOOKUP_MAP.set(entry.localizedNames.ceb.trim().toLowerCase(), entry);
  for (const alias of entry.aliases) {
    BOOK_LOOKUP_MAP.set(alias.trim().toLowerCase(), entry);
  }
}

export function resolveCanonicalBook(rawBookName: string): BibleBookRegistryEntry | null {
  if (!rawBookName || typeof rawBookName !== 'string') return null;
  const cleaned = rawBookName
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/^the\s+book\s+of\s+/i, '')
    .toLowerCase();
  return BOOK_LOOKUP_MAP.get(cleaned) || null;
}

export interface ParsedScriptureReference {
  isValid: boolean;
  bookEntry: BibleBookRegistryEntry | null;
  canonicalBookName: string;
  testament: CanonicalTestament;
  chapter: number;
  verseStart: number;
  verseEnd: number;
  normalizedReference: string;
  errorReason?: string;
}

/**
 * Validates that a Bible reference points to a real canonical book (1..66),
 * a valid chapter within that book, and a valid verse (or verse range) within that chapter.
 */
export function parseAndValidateScriptureReference(reference: string): ParsedScriptureReference {
  const invalid = (reason: string): ParsedScriptureReference => ({
    isValid: false,
    bookEntry: null,
    canonicalBookName: '',
    testament: 'Old Testament',
    chapter: 0,
    verseStart: 0,
    verseEnd: 0,
    normalizedReference: '',
    errorReason: reason,
  });

  if (!reference || typeof reference !== 'string') {
    return invalid('Empty or non-string scripture reference');
  }

  const cleaned = reference.trim().replace(/[–—]/g, '-');
  // Match e.g. "Genesis 6:14", "1 Samuel 17:4-7", "Obadiah 1:15", "Psalm 23:1"
  const match = cleaned.match(/^((?:[1-3]\s+)?[A-Za-z\s]+?)\s+(\d+)(?::(\d+)(?:-(\d+))?)?$/);
  if (!match) {
    return invalid(`Malformed reference format: "${reference}"`);
  }

  const rawBook = match[1].trim();
  const chapter = parseInt(match[2], 10);
  const verseStart = match[3] ? parseInt(match[3], 10) : 1;
  const verseEnd = match[4] ? parseInt(match[4], 10) : verseStart;

  const bookEntry = resolveCanonicalBook(rawBook);
  if (!bookEntry) {
    return invalid(`Book "${rawBook}" does not exist in the 66-book Protestant canon`);
  }

  if (isNaN(chapter) || chapter < 1 || chapter > bookEntry.chaptersCount) {
    return invalid(
      `Invalid chapter ${chapter} for ${bookEntry.bookName} (valid chapters: 1–${bookEntry.chaptersCount})`
    );
  }

  const maxVersesInChapter = bookEntry.verseCountsByChapter[chapter - 1] || 0;
  if (isNaN(verseStart) || verseStart < 1 || verseStart > maxVersesInChapter) {
    return invalid(
      `Invalid verse ${verseStart} in ${bookEntry.bookName} ${chapter} (valid verses: 1–${maxVersesInChapter})`
    );
  }

  if (isNaN(verseEnd) || verseEnd < verseStart || verseEnd > maxVersesInChapter) {
    return invalid(
      `Invalid verse range ${verseStart}-${verseEnd} in ${bookEntry.bookName} ${chapter} (max verse: ${maxVersesInChapter})`
    );
  }

  const normalizedReference =
    verseEnd > verseStart
      ? `${bookEntry.bookName} ${chapter}:${verseStart}-${verseEnd}`
      : `${bookEntry.bookName} ${chapter}:${verseStart}`;

  return {
    isValid: true,
    bookEntry,
    canonicalBookName: bookEntry.bookName,
    testament: bookEntry.testament,
    chapter,
    verseStart,
    verseEnd,
    normalizedReference,
  };
}

/**
 * AUTHORITATIVE WORLD ENGLISH BIBLE (WEB - Public Domain) SCRIPTURE CORPUS
 * Provides local deterministic passage verification across all 66 canonical books.
 */
export const WEB_SCRIPTURE_CORPUS: Record<string, string> = {
  'Genesis 1:1': 'In the beginning, God created the heavens and the earth.',
  'Genesis 6:14': 'Make a ship of gopher wood. You shall make rooms in the ship, and shall seal it inside and outside with pitch.',
  'Genesis 12:1': 'Now Yahweh said to Abram, "Leave your country, and your relatives, and your father\'s house, and go to the land that I will show you."',
  'Genesis 37:3': 'Now Israel loved Joseph more than all his children, because he was the son of his old age, and he made him a tunic of many colors.',
  'Exodus 3:2': 'Yahweh\'s angel appeared to him in a flame of fire out of the middle of a bush. He looked, and behold, the bush burned with fire, and the bush was not consumed.',
  'Exodus 12:13': 'The blood shall be to you for a token on the houses where you are. When I see the blood, I will pass over you, and no plague will be on you to destroy you when I strike the land of Egypt.',
  'Exodus 20:3': 'You shall have no other gods before me.',
  'Leviticus 16:21': 'Aaron shall lay both his hands on the head of the live goat, and confess over him all the iniquities of the children of Israel.',
  'Leviticus 19:18': 'You shall not take vengeance, nor bear any grudge against the children of your people; but you shall love your neighbor as yourself. I am Yahweh.',
  'Numbers 6:24-26': 'Yahweh bless you, and keep you. Yahweh make his face to shine on you, and be gracious to you. Yahweh lift up his face toward you, and give you peace.',
  'Numbers 21:9': 'Moses made a serpent of bronze, and set it on the standard; and it happened, that if a serpent had bitten any man, when he looked at the serpent of bronze, he lived.',
  'Deuteronomy 6:4-5': 'Hear, Israel: Yahweh is our God. Yahweh is one. You shall love Yahweh your God with all your heart, with all your soul, and with all your might.',
  'Deuteronomy 34:5': 'So Moses the servant of Yahweh died there in the land of Moab, according to Yahweh\'s word.',
  'Joshua 1:9': 'Haven\'t I commanded you? Be strong and courageous. Don\'t be afraid. Don\'t be dismayed, for Yahweh your God is with you wherever you go.',
  'Joshua 6:20': 'So the people shouted, and the priests blew the trumpets. When the people heard the sound of the trumpet, the people shouted with a great shout, and the wall fell down flat.',
  'Judges 4:4': 'Now Deborah, a prophetess, the wife of Lappidoth, judged Israel at that time.',
  'Judges 7:7': 'Yahweh said to Gideon, "I will save you by the three hundred men who lapped, and deliver the Midianites into your hand."',
  'Ruth 1:16': 'Ruth said, "Don\'t urge me to leave you, and to return from following you, for where you go, I will go; and where you stay, I will stay. Your people will be my people, and your God my God."',
  '1 Samuel 3:10': 'Yahweh came, and stood, and called as at other times, "Samuel! Samuel!" Then Samuel said, "Speak; for your servant hears."',
  '1 Samuel 17:49': 'David put his hand in his bag, took a stone from there, and slung it, and struck the Philistine in his forehead; and the stone sank into his forehead, and he fell on his face to the earth.',
  '2 Samuel 5:4': 'David was thirty years old when he began to reign, and he reigned forty years.',
  '2 Samuel 7:12-13': 'I will set up your offspring after you, who will proceed out of your body, and I will establish his kingdom. He will build a house for my name.',
  '1 Kings 3:9': 'Give your servant therefore an understanding heart to judge your people, that I may discern between good and evil.',
  '1 Kings 18:38': 'Then Yahweh\'s fire fell, and consumed the burnt offering, and the wood, and the stones, and the dust, and licked up the water that was in the trench.',
  '2 Kings 2:11': 'Behold, a chariot of fire and horses of fire separated them; and Elijah went up by a whirlwind into heaven.',
  '2 Kings 5:14': 'Then he went down, and dipped himself seven times in the Jordan, according to the saying of the man of God; and his flesh was restored like the flesh of a little child, and he was clean.',
  '1 Chronicles 16:8': 'Oh give thanks to Yahweh. Call on his name. Make his doings known among the peoples.',
  '1 Chronicles 29:11': 'Yours, Yahweh, is the greatness, the power, the glory, the victory, and the majesty: for all that is in the heavens and in the earth is yours.',
  '2 Chronicles 7:14': 'If my people, who are called by my name, will humble themselves, pray, seek my face, and turn from their wicked ways, then I will hear from heaven, will forgive their sin, and will heal their land.',
  'Ezra 1:1': 'Now in the first year of Cyrus king of Persia, that Yahweh\'s word by the mouth of Jeremiah might be accomplished, Yahweh stirred up the spirit of Cyrus king of Persia.',
  'Ezra 7:10': 'For Ezra had set his heart to seek Yahweh\'s law, and to do it, and to teach in Israel statutes and ordinances.',
  'Nehemiah 2:17': 'Then I said to them, "You see the bad situation that we are in, how Jerusalem lies waste, and its gates are burned with fire. Come, let\'s build up the wall of Jerusalem, that we won\'t be disgraced."',
  'Nehemiah 6:15': 'So the wall was finished in the twenty-fifth day of the month Elul, in fifty-two days.',
  'Esther 4:14': 'For if you remain silent now, then relief and deliverance will come to the Jews from another place, but you and your father\'s house will perish. Who knows if you haven\'t come to the kingdom for such a time as this?',
  'Job 1:21': 'He said, "Naked I came out of my mother\'s womb, and naked shall I return there. Yahweh gave, and Yahweh has taken away. Blessed be Yahweh\'s name."',
  'Job 19:25': 'But as for me, I know that my Redeemer lives. In the end, he will stand upon the earth.',
  'Psalms 23:1': 'Yahweh is my shepherd: I shall lack nothing.',
  'Psalms 119:105': 'Your word is a lamp to my feet, and a light for my path.',
  'Proverbs 1:7': 'The fear of Yahweh is the beginning of knowledge; but the foolish despise wisdom and instruction.',
  'Proverbs 3:5-6': 'Trust in Yahweh with all your heart, and don\'t lean on your own understanding. In all your ways acknowledge him, and he will make your paths straight.',
  'Ecclesiastes 3:1': 'For everything there is a season, and a time for every purpose under heaven.',
  'Ecclesiastes 12:13': 'This is the end of the matter. All has been heard. Fear God and keep his commandments; for this is the whole duty of man.',
  'Song of Solomon 2:1': 'I am a rose of Sharon, a lily of the valleys.',
  'Isaiah 9:6': 'For to us a child is born. To us a son is given; and the government will be on his shoulders. His name will be called Wonderful Counselor, Mighty God, Everlasting Father, Prince of Peace.',
  'Isaiah 53:5': 'But he was pierced for our transgressions. He was crushed for our iniquities. The punishment that brought our peace was on him; and by his wounds we are healed.',
  'Jeremiah 1:5': 'Before I formed you in the belly, I knew you. Before you came out of the womb, I sanctified you. I have appointed you a prophet to the nations.',
  'Jeremiah 29:11': 'For I know the thoughts that I think toward you, says Yahweh, thoughts of peace, and not of evil, to give you hope and a future.',
  'Lamentations 3:22-23': 'It is because of Yahweh\'s loving kindnesses that we are not consumed, because his compassion doesn\'t fail. They are new every morning. Great is your faithfulness.',
  'Ezekiel 36:26': 'I will also give you a new heart, and I will put a new spirit within you. I will take away the stony heart out of your flesh, and I will give you a heart of flesh.',
  'Ezekiel 37:3': 'He said to me, "Son of man, can these bones live?" I answered, "Lord Yahweh, you know."',
  'Daniel 3:25': 'He answered, "Look, I see four men loose, walking in the middle of the fire, and they are unharmed; and the aspect of the fourth is like a son of the gods."',
  'Daniel 6:22': 'My God hath sent his angel, and hath shut the lions\' mouths, that they have not hurt me.',
  'Hosea 6:6': 'For I desire mercy, and not sacrifice; and the knowledge of God more than burnt offerings.',
  'Joel 2:28': 'It will happen afterward, that I will pour out my Spirit on all flesh; and your sons and your daughters will prophesy.',
  'Amos 5:24': 'But let justice roll on like rivers, and righteousness like a mighty stream.',
  'Obadiah 1:4': 'Though you mount on high as the eagle, and though your nest is set among the stars, I will bring you down from there, says Yahweh.',
  'Jonah 1:17': 'Yahweh prepared a great fish to swallow up Jonah, and Jonah was in the belly of the fish three days and three nights.',
  'Micah 5:2': 'But you, Bethlehem Ephrathah, being small among the clans of Judah, out of you one will come out to me that is to be ruler in Israel.',
  'Micah 6:8': 'He has shown you, O man, what is good. What does Yahweh require of you, but to act justly, to love mercy, and to walk humbly with your God?',
  'Nahum 1:7': 'Yahweh is good, a stronghold in the day of trouble; and he knows those who take refuge in him.',
  'Habakkuk 2:4': 'Behold, his soul is puffed up. It is not upright in him, but the righteous will live by his faith.',
  'Zephaniah 3:17': 'Yahweh, your God, is among you, a mighty one who will save. He will rejoice over you with joy.',
  'Haggai 2:9': 'The latter glory of this house will be greater than the former, says Yahweh of Armies; and in this place I will give peace.',
  'Zechariah 4:6': 'Not by might, nor by power, but by my Spirit, says Yahweh of Armies.',
  'Zechariah 9:9': 'Rejoice greatly, daughter of Zion! Shout, daughter of Jerusalem! Behold, your King comes to you! He is righteous, and having salvation; lowly, and riding on a donkey.',
  'Malachi 3:10': 'Bring the whole tithe into the storehouse, that there may be food in my house, and test me now in this, says Yahweh of Armies.',
  'Matthew 5:3': 'Blessed are the poor in spirit, for theirs is the Kingdom of Heaven.',
  'Matthew 28:19': 'Go and make disciples of all nations, baptizing them in the name of the Father and of the Son and of the Holy Spirit.',
  'Mark 10:45': 'For even the Son of Man came not to be served, but to serve, and to give his life as a ransom for many.',
  'Mark 16:15': 'He said to them, "Go into all the world, and preach the Good News to the whole creation."',
  'Luke 2:11': 'For there is born to you today, in David\'s city, a Savior, who is Christ the Lord.',
  'Luke 10:33': 'But a certain Samaritan, as he traveled, came where he was. When he saw him, he was moved with compassion.',
  'Luke 19:10': 'For the Son of Man came to seek and to save that which was lost.',
  'John 1:1': 'In the beginning was the Word, and the Word was with God, and the Word was God.',
  'John 3:16': 'For God so loved the world, that he gave his one and only Son, that whoever believes in him should not perish, but have eternal life.',
  'John 14:6': 'Jesus said to him, "I am the way, the truth, and the life. No one comes to the Father, except through me."',
  'Acts 1:8': 'But you will receive power when the Holy Spirit has come upon you. You will be witnesses to me in Jerusalem, in all Judea and Samaria, and to the uttermost parts of the earth.',
  'Acts 2:4': 'They were all filled with the Holy Spirit, and began to speak with other languages, as the Spirit gave them the ability to speak.',
  'Romans 3:23': 'For all have sinned, and fall short of the glory of God.',
  'Romans 8:28': 'We know that all things work together for good for those who love God, for those who are called according to his purpose.',
  '1 Corinthians 13:4': 'Love is patient and is kind. Love doesn\'t envy. Love doesn\'t brag, is not proud.',
  '1 Corinthians 13:13': 'But now faith, hope, and love remain—these three. The greatest of these is love.',
  '2 Corinthians 5:7': 'For we walk by faith, not by sight.',
  '2 Corinthians 5:17': 'Therefore if anyone is in Christ, he is a new creation. The old things have passed away. Behold, all things have become new.',
  'Galatians 5:22-23': 'But the fruit of the Spirit is love, joy, peace, patience, kindness, goodness, faith, gentleness, and self-control. Against such things there is no law.',
  'Ephesians 2:8-9': 'For by grace you have been saved through faith, and that not of yourselves; it is the gift of God, not of works, that no one would boast.',
  'Ephesians 6:11': 'Put on the whole armor of God, that you may be able to stand against the wiles of the devil.',
  'Philippians 4:13': 'I can do all things through Christ, who strengthens me.',
  'Colossians 3:23': 'And whatever you do, work heartily, as for the Lord, and not for men.',
  '1 Thessalonians 5:16-18': 'Always rejoice. Pray without ceasing. In everything give thanks, for this is the will of God in Christ Jesus toward you.',
  '2 Thessalonians 3:3': 'But the Lord is faithful, who will establish you and guard you from the evil one.',
  '1 Timothy 4:12': 'Let no man despise your youth; but be an example to those who believe, in word, in your way of life, in love, in spirit, in faith, and in purity.',
  '2 Timothy 3:16': 'Every Scripture is God-breathed and profitable for teaching, for reproof, for correction, and for instruction in righteousness.',
  'Titus 3:5': 'Not by works of righteousness which we did ourselves, but according to his mercy, he saved us, through the washing of regeneration and renewing by the Holy Spirit.',
  'Philemon 1:10': 'I appeal to you for my child Onesimus, whom I have become the father of in my chains.',
  'Hebrews 11:1': 'Now faith is assurance of things hoped for, proof of things not seen.',
  'Hebrews 4:12': 'For the word of God is living and active, and sharper than any two-edged sword.',
  'James 1:22': 'But be doers of the word, and not only hearers, deluding your own selves.',
  'James 2:26': 'For as the body apart from the spirit is dead, even so faith apart from works is dead.',
  '1 Peter 5:7': 'Casting all your worries on him, because he cares for you.',
  '2 Peter 3:9': 'The Lord is not slow concerning his promise, as some count slowness; but is patient with us, not wishing that any should perish, but that all should come to repentance.',
  '1 John 4:8': 'He who doesn\'t love doesn\'t know God, for God is love.',
  '2 John 1:6': 'This is love, that we should walk according to his commandments.',
  '3 John 1:11': 'Beloved, don\'t imitate that which is evil, but that which is good. He who does good is of God.',
  'Jude 1:3': 'Beloved, while I was very eager to write to you about our common salvation, I was constrained to write to you exhorting you to contend earnestly for the faith which was once for all delivered to the saints.',
  'Revelation 1:8': '"I am the Alpha and the Omega," says the Lord God, "who is and who was and who is to come, the Almighty."',
  'Revelation 21:4': 'He will wipe away every tear from their eyes. Death will be no more; neither will there be mourning, nor crying, nor pain, any more.',
};

function makeFull66SeedItem(params: {
  questionCode: string;
  category: CanonicalGenerationCategory;
  bibleBook: string;
  testament: CanonicalTestament;
  difficulty: 'Beginner' | 'Easy' | 'Intermediate' | 'Advanced' | 'Expert';
  scriptureReference: string;
  en: { questionText: string; options: string[]; correctAnswer: string; explanation: string };
  tl: { questionText: string; options: string[]; correctAnswer: string; explanation: string };
  ceb: { questionText: string; options: string[]; correctAnswer: string; explanation: string };
}): SeedQuestionItem {
  return {
    questionCode: params.questionCode,
    category: params.category,
    bibleBook: params.bibleBook,
    testament: params.testament,
    difficulty: params.difficulty,
    questionFormat: 'Multiple Choice',
    scriptureReference: params.scriptureReference,
    en: params.en,
    tl: params.tl,
    ceb: params.ceb,
    ko: params.en,
    'zh-CN': params.en,
    'zh-TW': params.en,
    es: params.en,
  };
}

/**
 * COMPLETE 66-BOOK VERIFIED EXPANSION QUESTIONS
 * Ensures every single canonical book from Genesis (Book 1) through Revelation (Book 66)
 * has WEB-verified questions with English (en), Filipino (tl), and Cebuano (ceb) phrasings.
 */
export const CANONICAL_66_BOOK_SEED_QUESTIONS: SeedQuestionItem[] = [
  makeFull66SeedItem({
    questionCode: 'QB-66-NUM-001',
    category: 'Bible Events',
    bibleBook: 'Numbers',
    testament: 'Old Testament',
    difficulty: 'Easy',
    scriptureReference: 'Numbers 21:9',
    en: {
      questionText: 'In Numbers 21:9, what did Moses make and set on a standard so that anyone bitten by a fiery serpent could look at it and live?',
      options: ['A serpent of bronze', 'A golden calf', 'A silver trumpet', 'An olive branch'],
      correctAnswer: 'A serpent of bronze',
      explanation: 'Numbers 21:9 (WEB) states: "Moses made a serpent of bronze, and set it on the standard; and it happened, that if a serpent had bitten any man, when he looked at the serpent of bronze, he lived."',
    },
    tl: {
      questionText: 'Sa Mga Bilang 21:9, ano ang ginawa ni Moises at inilagay sa isang tikin upang ang sinumang makagat ng ahas na tanso ay mabuhay kapag tumingin dito?',
      options: ['Isang ahas na tanso', 'Isang gintong guya', 'Isang pilak na pakakak', 'Isang sanga ng olibo'],
      correctAnswer: 'Isang ahas na tanso',
      explanation: 'Ayon sa Mga Bilang 21:9, gumawa si Moises ng isang ahas na tanso at ipinutong sa isang tikin upang mabuhay ang sinumang tumingin dito.',
    },
    ceb: {
      questionText: 'Sa Numeros 21:9, unsa ang gibuhat ni Moises ug gibutang sa usa ka tukon aron ang bisan kinsa ngamapaakan sa bitin mabuhi kon motan-aw niini?',
      options: ['Usa ka bitin nga tumbaga', 'Usa ka bulawan nga nating baka', 'Usa ka pilak nga trumpeta', 'Usa ka sanga sa olibo'],
      correctAnswer: 'Usa ka bitin nga tumbaga',
      explanation: 'Ang Numeros 21:9 nag-ingon nga si Moises nagbuhat ug usa ka bitin nga tumbaga ug gibutang kini sa tukon.',
    },
  }),
  makeFull66SeedItem({
    questionCode: 'QB-66-DEU-001',
    category: 'Biblical Teachings',
    bibleBook: 'Deuteronomy',
    testament: 'Old Testament',
    difficulty: 'Easy',
    scriptureReference: 'Deuteronomy 6:4-5',
    en: {
      questionText: 'In the Shema of Deuteronomy 6:4-5, how are the people of Israel commanded to love Yahweh their God?',
      options: [
        'With all their heart, soul, and might',
        'Only during the Feast of Unleavened Bread',
        'Through burnt offerings of silver alone',
        'By building towers in every city',
      ],
      correctAnswer: 'With all their heart, soul, and might',
      explanation: 'Deuteronomy 6:4-5 (WEB) commands: "Hear, Israel: Yahweh is our God. Yahweh is one. You shall love Yahweh your God with all your heart, with all your soul, and with all your might."',
    },
    tl: {
      questionText: 'Sa Deuteronomio 6:4-5, paano iniutos sa Israel na ibigin ang Panginoon nilang Diyos?',
      options: [
        'Nang buong puso, buong kaluluwa, at buong lakas',
        'Sa panahon lamang ng Kapistahan ng Tinapay na Walang Lebadura',
        'Sa pamamagitan lamang ng handog na pilak',
        'Sa pagtatayo ng tore sa bawat lungsod',
      ],
      correctAnswer: 'Nang buong puso, buong kaluluwa, at buong lakas',
      explanation: 'Sinasabi sa Deuteronomio 6:5 na ibigin ang Panginoong Diyos nang buong puso, buong kaluluwa, at buong lakas.',
    },
    ceb: {
      questionText: 'Sa Deuteronomio 6:4-5, giunsa pagsugo ang Israel sa paghigugma sa Ginoo nga ilang Diyos?',
      options: [
        'Sa tibuok nilang kasingkasing, kalag, ug kusog',
        'Panahon lamang sa Kasaulogan sa Tinapay nga Walay Igpapatubo',
        'Pinaagi lamang sa halad nga pilak',
        'Pinaagi sa pagtukod ug tore sa matag siyudad',
      ],
      correctAnswer: 'Sa tibuok nilang kasingkasing, kalag, ug kusog',
      explanation: 'Ang Deuteronomio 6:5 nagsugo sa paghigugma sa Diyos sa tibuok kasingkasing, kalag, ug kusog.',
    },
  }),
  makeFull66SeedItem({
    questionCode: 'QB-66-JDG-001',
    category: 'Bible Characters',
    bibleBook: 'Judges',
    testament: 'Old Testament',
    difficulty: 'Intermediate',
    scriptureReference: 'Judges 7:7',
    en: {
      questionText: 'According to Judges 7:7, with how many men who lapped water did God promise Gideon He would deliver the Midianites into his hand?',
      options: ['300 men', '10,000 men', '32,000 men', '120 men'],
      correctAnswer: '300 men',
      explanation: 'Judges 7:7 (WEB) states: "Yahweh said to Gideon, \'I will save you by the three hundred men who lapped, and deliver the Midianites into your hand.\'"',
    },
    tl: {
      questionText: 'Ayon sa Mga Hukom 7:7, ilang lalaking humimod ng tubig ang ginamit ng Diyos sa pamumuno ni Gideon upang iligtas ang Israel laban sa mga Midianita?',
      options: ['300 lalaki', '10,000 lalaki', '32,000 lalaki', '120 lalaki'],
      correctAnswer: '300 lalaki',
      explanation: 'Sinabi ng Panginoon kay Gideon sa Mga Hukom 7:7 na sa pamamagitan ng tatlong daang lalaking humimod ng tubig ay ililigtas Niya sila.',
    },
    ceb: {
      questionText: 'Sumala sa Mga Maghuhukom 7:7, pila ka tawo nga mitilaab sa tubig ang gigamit sa Diyos uban kang Gideon aron pildihon ang mga Midianhon?',
      options: ['300 ka tawo', '10,000 ka tawo', '32,000 ka tawo', '120 ka tawo'],
      correctAnswer: '300 ka tawo',
      explanation: 'Sa Mga Maghuhukom 7:7, ang Diyos miingon kang Gideon nga luwason Niya sila pinaagi sa 300 ka tawo.',
    },
  }),
  makeFull66SeedItem({
    questionCode: 'QB-66-RUT-001',
    category: 'Bible Characters',
    bibleBook: 'Ruth',
    testament: 'Old Testament',
    difficulty: 'Easy',
    scriptureReference: 'Ruth 1:16',
    en: {
      questionText: 'To whom did Ruth speak the famous words in Ruth 1:16: "Where you go, I will go... Your people will be my people, and your God my God"?',
      options: ['Naomi', 'Orpah', 'Hannah', 'Miriam'],
      correctAnswer: 'Naomi',
      explanation: 'In Ruth 1:16 (WEB), Ruth spoke these words of loyalty to her mother-in-law Naomi as they returned toward Bethlehem.',
    },
    tl: {
      questionText: 'Kanino sinabi ni Ruth sa Ruth 1:16 ang mga salitang: "Kung saan ka pupunta ay pupunta ako... ang iyong bayan ay magiging aking bayan, at ang iyong Diyos ay aking Diyos"?',
      options: ['Naomi', 'Orpa', 'Ana', 'Miriam'],
      correctAnswer: 'Naomi',
      explanation: 'Sa Ruth 1:16, ipinahayag ni Ruth ang kanyang katapatan sa kanyang biyenang si Naomi.',
    },
    ceb: {
      questionText: 'Kang kinsa gisulti ni Ruth sa Ruth 1:16 ang mga pulong: "Kon asa ikaw moadto, moadto usab ako... ang imong katawhan mahimong akong katawhan, ug ang imong Diyos akong Diyos"?',
      options: ['Naomi', 'Orpa', 'Ana', 'Miriam'],
      correctAnswer: 'Naomi',
      explanation: 'Sa Ruth 1:16, gisulti kini ni Ruth ngadto sa iyang ugangan nga si Naomi.',
    },
  }),
  makeFull66SeedItem({
    questionCode: 'QB-66-2SA-001',
    category: 'Bible History',
    bibleBook: '2 Samuel',
    testament: 'Old Testament',
    difficulty: 'Intermediate',
    scriptureReference: '2 Samuel 5:4',
    en: {
      questionText: 'According to 2 Samuel 5:4, how old was David when he began to reign as king, and how many years did he reign in total?',
      options: [
        '30 years old when he began, and reigned 40 years',
        '20 years old when he began, and reigned 50 years',
        '40 years old when he began, and reigned 30 years',
        '25 years old when he began, and reigned 33 years',
      ],
      correctAnswer: '30 years old when he began, and reigned 40 years',
      explanation: '2 Samuel 5:4 (WEB) states: "David was thirty years old when he began to reign, and he reigned forty years."',
    },
    tl: {
      questionText: 'Ayon sa 2 Samuel 5:4, ilang taong gulang si David nang siya ay magsimulang maghari, at ilang taon siyang naghari?',
      options: [
        '30 taong gulang nang magsimula, at naghari nang 40 taon',
        '20 taong gulang nang magsimula, at naghari nang 50 taon',
        '40 taong gulang nang magsimula, at naghari nang 30 taon',
        '25 taong gulang nang magsimula, at naghari nang 33 taon',
      ],
      correctAnswer: '30 taong gulang nang magsimula, at naghari nang 40 taon',
      explanation: 'Nakatala sa 2 Samuel 5:4 na tatlumpung taong gulang si David nang magsimulang maghari, at siya ay naghari nang apatnapung taon.',
    },
    ceb: {
      questionText: 'Sumala sa 2 Samuel 5:4, pilay panuigon ni David sa pagsugod niya paghari, ug pila ka tuig siya naghari?',
      options: [
        '30 ka tuig ang panuigon sa pagsugod, ug naghari sulod sa 40 ka tuig',
        '20 ka tuig ang panuigon sa pagsugod, ug naghari sulod sa 50 ka tuig',
        '40 ka tuig ang panuigon sa pagsugod, ug naghari sulod sa 30 ka tuig',
        '25 ka tuig ang panuigon sa pagsugod, ug naghari sulod sa 33 ka tuig',
      ],
      correctAnswer: '30 ka tuig ang panuigon sa pagsugod, ug naghari sulod sa 40 ka tuig',
      explanation: 'Ang 2 Samuel 5:4 nag-ingon nga katloan ka tuig ang panuigon ni David sa pagsugod niya paghari ug naghari siya sulod sa kap-atan ka tuig.',
    },
  }),
  makeFull66SeedItem({
    questionCode: 'QB-66-2KI-001',
    category: 'Bible Events',
    bibleBook: '2 Kings',
    testament: 'Old Testament',
    difficulty: 'Easy',
    scriptureReference: '2 Kings 5:14',
    en: {
      questionText: 'In 2 Kings 5:14, how many times did Naaman dip himself in the Jordan River according to Elisha\'s word to be healed of leprosy?',
      options: ['Seven times', 'Three times', 'Twelve times', 'Forty times'],
      correctAnswer: 'Seven times',
      explanation: '2 Kings 5:14 (WEB) records: "Then he went down, and dipped himself seven times in the Jordan, according to the saying of the man of God; and his flesh was restored."',
    },
    tl: {
      questionText: 'Sa 2 Mga Hari 5:14, ilang ulit lumubog si Naaman sa Ilog Jordan ayon sa salita ni Eliseo upang gumaling sa kanyang ketong?',
      options: ['Pitong ulit', 'Tatlong ulit', 'Labindalawang ulit', 'Apatnapung ulit'],
      correctAnswer: 'Pitong ulit',
      explanation: 'Ayon sa 2 Mga Hari 5:14, lumubog si Naaman nang pitong ulit sa Ilog Jordan at ang kanyang laman ay naging malinis.',
    },
    ceb: {
      questionText: 'Sa 2 Mga Hari 5:14, kapila mitunlob si Naaman sa Suba sa Jordan sumala sa pulong ni Eliseo aron maayo sa sanla?',
      options: ['Makapito', 'Makatulo', 'Napulo ug duha ka higayon', 'Kap-atan ka higayon'],
      correctAnswer: 'Makapito',
      explanation: 'Ang 2 Mga Hari 5:14 nagtala nga si Naaman mitunlob sa makapito sa Jordan ug naayo ang iyang panit.',
    },
  }),
  makeFull66SeedItem({
    questionCode: 'QB-66-1CH-001',
    category: 'Biblical Teachings',
    bibleBook: '1 Chronicles',
    testament: 'Old Testament',
    difficulty: 'Intermediate',
    scriptureReference: '1 Chronicles 29:11',
    en: {
      questionText: 'In David\'s prayer of praise in 1 Chronicles 29:11, what five attributes does he declare belong to Yahweh across heaven and earth?',
      options: [
        'Greatness, power, glory, victory, and majesty',
        'Gold, silver, bronze, iron, and cedar',
        'Chariots, horsemen, shields, spears, and bows',
        'Clouds, thunder, wind, rain, and harvest',
      ],
      correctAnswer: 'Greatness, power, glory, victory, and majesty',
      explanation: '1 Chronicles 29:11 (WEB) declares: "Yours, Yahweh, is the greatness, the power, the glory, the victory, and the majesty: for all that is in the heavens and in the earth is yours."',
    },
    tl: {
      questionText: 'Sa panalangin ng papuri ni David sa 1 Mga Cronica 29:11, ano ang limang katangiang ipinahayag niyang pag-aari ng Panginoon sa langit at lupa?',
      options: [
        'Kadakilaan, kapangyarihan, kaluwalhatian, tagumpay, at kamahalan',
        'Ginto, pilak, tanso, bakal, at sedro',
        'Mga karwahe, mangangabayo, kalasag, sibat, at busog',
        'Mga ulap, kulog, hangin, ulan, at ani',
      ],
      correctAnswer: 'Kadakilaan, kapangyarihan, kaluwalhatian, tagumpay, at kamahalan',
      explanation: 'Sa 1 Mga Cronica 29:11, ipinahayag ni David na sa Panginoon ang kadakilaan, kapangyarihan, kaluwalhatian, tagumpay, at kamahalan.',
    },
    ceb: {
      questionText: 'Sa pag-ampo sa pagdayeg ni David sa 1 Mga Cronicas 29:11, unsang lima ka hiyas ang iyang gipahayag nga iya sa Ginoo sa langit ug yuta?',
      options: [
        'Ang pagkadako, gahom, himaya, kadaogan, ug pagkahalangdon',
        'Bulawan, pilak, tumbaga, puthaw, ug sedro',
        'Mga karwahe, mangangabayo, taming, bangkaw, ug pana',
        'Mga panganod, dalugdog, hangin, ulan, ug ani',
      ],
      correctAnswer: 'Ang pagkadako, gahom, himaya, kadaogan, ug pagkahalangdon',
      explanation: 'Ang 1 Mga Cronicas 29:11 nagpahayag nga iya sa Ginoo ang pagkadako, gahom, himaya, kadaogan, ug pagkahalangdon.',
    },
  }),
  makeFull66SeedItem({
    questionCode: 'QB-66-2CH-001',
    category: 'Biblical Teachings',
    bibleBook: '2 Chronicles',
    testament: 'Old Testament',
    difficulty: 'Easy',
    scriptureReference: '2 Chronicles 7:14',
    en: {
      questionText: 'In 2 Chronicles 7:14, what does God promise to do when His people humble themselves, pray, seek His face, and turn from their wicked ways?',
      options: [
        'Hear from heaven, forgive their sin, and heal their land',
        'Send fire upon the surrounding mountains',
        'Double the gold in the royal treasury',
        'Move the temple to Mount Sinai',
      ],
      correctAnswer: 'Hear from heaven, forgive their sin, and heal their land',
      explanation: '2 Chronicles 7:14 (WEB) promises: "then I will hear from heaven, will forgive their sin, and will heal their land."',
    },
    tl: {
      questionText: 'Sa 2 Mga Cronica 7:14, ano ang ipinangako ng Diyos kapag ang Kanyang bayan ay nagpakumbaba, nanalangin, humanap sa Kanyang mukha, at tumalikod sa masamang gawa?',
      options: [
        'Diringgin mula sa langit, patatawarin ang kasalanan, at pagagalingin ang kanilang lupain',
        'Magpapadala ng apoy sa mga bundok',
        'Dodoblehin ang ginto sa kabang-yaman',
        'Ililipat ang templo sa Bundok Sinai',
      ],
      correctAnswer: 'Diringgin mula sa langit, patatawarin ang kasalanan, at pagagalingin ang kanilang lupain',
      explanation: 'Sa 2 Mga Cronica 7:14, nangako ang Diyos na diringgin Niya mula sa langit, patatawarin ang kanilang kasalanan, at pagagalingin ang kanilang lupain.',
    },
    ceb: {
      questionText: 'Sa 2 Mga Cronicas 7:14, unsay gisaad sa Diyos kon ang Iyang katawhan magpaubos, mag-ampo, mangita sa Iyang nawong, ug mobiya sa ilang daotang dalan?',
      options: [
        'Magpatalinghog gikan sa langit, mopasaylo sa ilang sala, ug moayo sa ilang yuta',
        'Magpadala ug kalayo sa kabukiran',
        'iloobon ang bulawan sa panudlanan',
        'Ibalhin ang templo ngadto sa Bukid sa Sinai',
      ],
      correctAnswer: 'Magpatalinghog gikan sa langit, mopasaylo sa ilang sala, ug moayo sa ilang yuta',
      explanation: 'Ang 2 Mga Cronicas 7:14 nagsaad nga ang Diyos maminaw gikan sa langit, mopasaylo sa ilang sala, ug moayo sa ilang yuta.',
    },
  }),
  makeFull66SeedItem({
    questionCode: 'QB-66-EZR-001',
    category: 'Bible History',
    bibleBook: 'Ezra',
    testament: 'Old Testament',
    difficulty: 'Intermediate',
    scriptureReference: 'Ezra 7:10',
    en: {
      questionText: 'According to Ezra 7:10, what three things had Ezra set his heart to do regarding Yahweh\'s law?',
      options: [
        'To seek Yahweh\'s law, to do it, and to teach in Israel statutes and ordinances',
        'To translate it into Greek, seal it in jars, and hide it in caves',
        'To engrave it on bronze shields for the Persian army',
        'To recite it only once every seven years in Babylon',
      ],
      correctAnswer: 'To seek Yahweh\'s law, to do it, and to teach in Israel statutes and ordinances',
      explanation: 'Ezra 7:10 (WEB) states: "For Ezra had set his heart to seek Yahweh\'s law, and to do it, and to teach in Israel statutes and ordinances."',
    },
    tl: {
      questionText: 'Ayon sa Ezra 7:10, anong tatlong bagay ang inilagak ni Ezra sa kanyang puso na gawin tungkol sa kautusan ng Panginoon?',
      options: [
        'Saliksikin ang kautusan ng Panginoon, tuparin ito, at ituro sa Israel ang mga tuntunin at kahatulan',
        'Isalin ito sa Griyego, ilagay sa banga, at itago sa kuweba',
        'Iukit ito sa mga kalasag na tanso para sa hukbo ng Persia',
        'Basahin lamang ito tuwing ikapitong taon sa Babilonia',
      ],
      correctAnswer: 'Saliksikin ang kautusan ng Panginoon, tuparin ito, at ituro sa Israel ang mga tuntunin at kahatulan',
      explanation: 'Nakasaad sa Ezra 7:10 na inilagak ni Ezra ang kanyang puso upang saliksikin ang kautusan ng Panginoon, tuparin ito, at ituro sa Israel.',
    },
    ceb: {
      questionText: 'Sumala sa Esdras 7:10, unsang tulo ka butang ang gitinguha sa kasingkasing ni Esdras mahitungod sa balaod sa Ginoo?',
      options: [
        'Ang pagpangita sa balaod sa Ginoo, pagtuman niini, ug pagtudlo sa Israel sa mga tulumanon',
        'Ang paghubad niini sa Griyego ug pagtago sa langob',
        'Ang pagkulit niini sa mga taming nga tumbaga',
        'Ang pagbasa niini kausa lamang matag pito ka tuig sa Babilonia',
      ],
      correctAnswer: 'Ang pagpangita sa balaod sa Ginoo, pagtuman niini, ug pagtudlo sa Israel sa mga tulumanon',
      explanation: 'Ang Esdras 7:10 nag-ingon nga giandam ni Esdras ang iyang kasingkasing sa pagpangita sa balaod sa Ginoo, pagbuhat niini, ug pagtudlo sa Israel.',
    },
  }),
  makeFull66SeedItem({
    questionCode: 'QB-66-NEH-001',
    category: 'Bible History',
    bibleBook: 'Nehemiah',
    testament: 'Old Testament',
    difficulty: 'Intermediate',
    scriptureReference: 'Nehemiah 6:15',
    en: {
      questionText: 'According to Nehemiah 6:15, in how many days was the rebuilding of the wall of Jerusalem finished under Nehemiah\'s leadership?',
      options: ['52 days', '40 days', '70 days', '120 days'],
      correctAnswer: '52 days',
      explanation: 'Nehemiah 6:15 (WEB) records: "So the wall was finished in the twenty-fifth day of the month Elul, in fifty-two days."',
    },
    tl: {
      questionText: 'Ayon sa Nehemias 6:15, sa loob ng ilang araw natapos ang muling pagtatayo ng pader ng Jerusalem sa pamumuno ni Nehemias?',
      options: ['52 araw', '40 araw', '70 araw', '120 araw'],
      correctAnswer: '52 araw',
      explanation: 'Ayon sa Nehemias 6:15, ang pader ay natapos sa ikadalawampu\'t limang araw ng buwan ng Elul, sa loob ng limampu\'t dalawang (52) araw.',
    },
    ceb: {
      questionText: 'Sumala sa Nehemias 6:15, sulod sa pila ka adlaw nahuman ang pagtukod pag-usab sa paril sa Jerusalem ubos sa pagpangulo ni Nehemias?',
      options: ['52 ka adlaw', '40 ka adlaw', '70 ka adlaw', '120 ka adlaw'],
      correctAnswer: '52 ka adlaw',
      explanation: 'Ang Nehemias 6:15 nagtala nga ang paril nahuman sulod sa kalim-an ug duha (52) ka adlaw.',
    },
  }),
  makeFull66SeedItem({
    questionCode: 'QB-66-EST-001',
    category: 'Bible Characters',
    bibleBook: 'Esther',
    testament: 'Old Testament',
    difficulty: 'Easy',
    scriptureReference: 'Esther 4:14',
    en: {
      questionText: 'Who urged Queen Esther with the words in Esther 4:14: "Who knows if you haven\'t come to the kingdom for such a time as this?"',
      options: ['Mordecai', 'Haman', 'Harbona', 'Nehemiah'],
      correctAnswer: 'Mordecai',
      explanation: 'In Esther 4:14 (WEB), Mordecai sent this message to Queen Esther, challenging her to intercede before the king on behalf of her people.',
    },
    tl: {
      questionText: 'Sino ang nagpadala ng mensahe kay Reyna Ester sa Ester 4:14 na nagsasabing: "Sino ang nakaaalam kung ikaw ay nalagay sa kaharian para sa ganitong panahon?"',
      options: ['Mardoqueo', 'Haman', 'Harbona', 'Nehemias'],
      correctAnswer: 'Mardoqueo',
      explanation: 'Sa Ester 4:14, hinikayat ni Mardoqueo si Reyna Ester na mamagitan para sa mga Judio.',
    },
    ceb: {
      questionText: 'Kinsa ang nag-ingon kang Rayna Ester sa Ester 4:14: "Kinsa bay nasayod kon ikaw nahiabot sa gingharian alang sa ingon niini nga panahon?"',
      options: ['Mardoqueo', 'Haman', 'Harbona', 'Nehemias'],
      correctAnswer: 'Mardoqueo',
      explanation: 'Sa Ester 4:14, si Mardoqueo ang nagdasig kang Rayna Ester sa pagtabang sa iyang katawhan.',
    },
  }),
  makeFull66SeedItem({
    questionCode: 'QB-66-JOB-001',
    category: 'Biblical Teachings',
    bibleBook: 'Job',
    testament: 'Old Testament',
    difficulty: 'Easy',
    scriptureReference: 'Job 19:25',
    en: {
      questionText: 'In Job 19:25, what confident declaration of faith does Job make in the midst of his suffering?',
      options: [
        'I know that my Redeemer lives, and in the end he will stand upon the earth',
        'My wealth will be restored by the merchants of Tarshish',
        'No man can ever behold the light of God',
        'I will flee by ship to the land of Egypt',
      ],
      correctAnswer: 'I know that my Redeemer lives, and in the end he will stand upon the earth',
      explanation: 'Job 19:25 (WEB) states: "But as for me, I know that my Redeemer lives. In the end, he will stand upon the earth."',
    },
    tl: {
      questionText: 'Sa Job 19:25, anong matibay na pahayag ng pananampalataya ang sinabi ni Job sa gitna ng kanyang pagdurusa?',
      options: [
        'Alam kong ang aking Manunubos ay buhay, at sa wakas ay tatayo Siya sa ibabaw ng lupa',
        'Ang aking kayamanan ay ibabalik ng mga mangangalakal ng Tarsis',
        'Walang taong makakakita sa liwanag ng Diyos',
        'Tatakas ako sakay ng barko patungong Ehipto',
      ],
      correctAnswer: 'Alam kong ang aking Manunubos ay buhay, at sa wakas ay tatayo Siya sa ibabaw ng lupa',
      explanation: 'Ipinahayag ni Job sa Job 19:25: "Ngunit talastas ko na ang aking Manunubos ay nabubuhay, at sa wakas ay tatayo siya sa ibabaw ng lupa."',
    },
    ceb: {
      questionText: 'Sa Job 19:25, unsang lig-on nga pahayag sa pagtuo ang gisulti ni Job taliwala sa iyang pag-antos?',
      options: [
        'Ako nasayod nga ang akong Manunubos buhi, ug sa kataposan Siya mobarog sa ibabaw sa yuta',
        'Ang akong bahandi ibalik sa mga magpapatigayon sa Tarsis',
        'Walay tawo nga makakita sa kahayag sa Diyos',
        'Molayas ako sakay sa barko paingon sa Ehipto',
      ],
      correctAnswer: 'Ako nasayod nga ang akong Manunubos buhi, ug sa kataposan Siya mobarog sa ibabaw sa yuta',
      explanation: 'Sa Job 19:25, si Job mipahayag nga buhi ang iyang Manunubos ug mobarog Siya sa ibabaw sa yuta.',
    },
  }),
  makeFull66SeedItem({
    questionCode: 'QB-66-ECC-001',
    category: 'Biblical Teachings',
    bibleBook: 'Ecclesiastes',
    testament: 'Old Testament',
    difficulty: 'Easy',
    scriptureReference: 'Ecclesiastes 12:13',
    en: {
      questionText: 'How does Ecclesiastes 12:13 summarize the conclusion of the matter and the whole duty of man?',
      options: [
        'Fear God and keep his commandments',
        'Gather gold and build great vineyards',
        'Study the stars and write many books',
        'Conquer neighboring kingdoms by the sword',
      ],
      correctAnswer: 'Fear God and keep his commandments',
      explanation: 'Ecclesiastes 12:13 (WEB) concludes: "This is the end of the matter. All has been heard. Fear God and keep his commandments; for this is the whole duty of man."',
    },
    tl: {
      questionText: 'Paano binuod ng Ang Mangangaral (Ecclesiastes) 12:13 ang wakas ng bagay at ang buong katungkulan ng tao?',
      options: [
        'Matakot sa Diyos at tuparin ang Kanyang mga utos',
        'Magtipon ng ginto at magtayo ng malalaking ubasan',
        'Pag-aralan ang mga bituin at sumulat ng maraming aklat',
        'Sakupin ang mga karatig na kaharian',
      ],
      correctAnswer: 'Matakot sa Diyos at tuparin ang Kanyang mga utos',
      explanation: 'Sinasabi sa Ang Mangangaral 12:13: "Matakot ka sa Diyos at sundin mo ang kanyang mga utos, sapagkat ito ang buong katungkulan ng tao."',
    },
    ceb: {
      questionText: 'Giunsa pagtingob sa Ecclesiastes 12:13 ang kataposan sa tanang butang ug ang tibuok katungdanan sa tawo?',
      options: [
        'Kahadloki ang Diyos ug tumana ang Iyang mga sugo',
        'Pagtigom ug bulawan ug pagtukod ug dagkong parasan',
        'Tun-i ang mga bituon ug pagsulat ug daghang basahon',
        'Sakopa ang silingang mga gingharian',
      ],
      correctAnswer: 'Kahadloki ang Diyos ug tumana ang Iyang mga sugo',
      explanation: 'Ang Ecclesiastes 12:13 nag-ingon: "Kahadloki ang Diyos ug bantayi ang iyang mga sugo, kay kini mao ang tibuok katungdanan sa tawo."',
    },
  }),
  makeFull66SeedItem({
    questionCode: 'QB-66-SOS-001',
    category: 'Biblical Knowledge',
    bibleBook: 'Song of Solomon',
    testament: 'Old Testament',
    difficulty: 'Intermediate',
    scriptureReference: 'Song of Solomon 2:1',
    en: {
      questionText: 'In Song of Solomon 2:1, what two flowers does the speaker compare herself to?',
      options: [
        'A rose of Sharon and a lily of the valleys',
        'An olive blossom and a cedar branch',
        'A pomegranate flower and a fig leaf',
        'A desert almond and a mountain myrtle',
      ],
      correctAnswer: 'A rose of Sharon and a lily of the valleys',
      explanation: 'Song of Solomon 2:1 (WEB) states: "I am a rose of Sharon, a lily of the valleys."',
    },
    tl: {
      questionText: 'Sa Awit ni Solomon 2:1, sa anong dalawang bulaklak inihambing ang nagsasalita?',
      options: [
        'Isang rosas ng Saron at isang liryo ng mga libis',
        'Bulaklak ng olibo at sanga ng sedro',
        'Bulaklak ng granada at dahon ng igos',
        'Almendras sa ilang at mirto sa bundok',
      ],
      correctAnswer: 'Isang rosas ng Saron at isang liryo ng mga libis',
      explanation: 'Sinasabi sa Awit ni Solomon 2:1: "Ako ay rosas ng Saron, at liryo sa mga libis."',
    },
    ceb: {
      questionText: 'Sa Awit ni Solomon 2:1, sa unsang duha ka bulak gitandi ang nagsulti?',
      options: [
        'Usa ka rosas sa Saron ug usa ka liryo sa mga walog',
        'Bulak sa olibo ug sanga sa sedro',
        'Bulak sa granada ug dahon sa igos',
        'Almendras sa kamingawan ug mirto sa bukid',
      ],
      correctAnswer: 'Usa ka rosas sa Saron ug usa ka liryo sa mga walog',
      explanation: 'Ang Awit ni Solomon 2:1 nag-ingon: "Ako mao ang rosas sa Saron, ang liryo sa mga walog."',
    },
  }),
  makeFull66SeedItem({
    questionCode: 'QB-66-JER-001',
    category: 'Prophecies and Fulfillments',
    bibleBook: 'Jeremiah',
    testament: 'Old Testament',
    difficulty: 'Easy',
    scriptureReference: 'Jeremiah 29:11',
    en: {
      questionText: 'In Jeremiah 29:11, what kind of thoughts does Yahweh declare He thinks toward His people?',
      options: [
        'Thoughts of peace, and not of evil, to give them hope and a future',
        'Thoughts of unending exile in Babylon forever',
        'Thoughts of military alliance with Pharaoh',
        'Thoughts of abandoning Jerusalem permanently',
      ],
      correctAnswer: 'Thoughts of peace, and not of evil, to give them hope and a future',
      explanation: 'Jeremiah 29:11 (WEB) states: "For I know the thoughts that I think toward you, says Yahweh, thoughts of peace, and not of evil, to give you hope and a future."',
    },
    tl: {
      questionText: 'Sa Jeremias 29:11, anong uri ng mga kaisipan o plano ang ipinahayag ng Panginoon para sa Kanyang bayan?',
      options: [
        'Mga kaisipan ng kapayapaan at hindi ng kasamaan, upang bigyan sila ng pag-asa at kinabukasan',
        'Mga kaisipan ng walang-hanggang pagkabihag sa Babilonia',
        'Mga kaisipan ng pakikipag-alyansa kay Faraon',
        'Mga kaisipan ng tuluyang pag-iwan sa Jerusalem',
      ],
      correctAnswer: 'Mga kaisipan ng kapayapaan at hindi ng kasamaan, upang bigyan sila ng pag-asa at kinabukasan',
      explanation: 'Sa Jeremias 29:11, sinabi ng Panginoon na ang Kanyang mga kaisipan ay ukol sa kapayapaan upang magbigay ng pag-asa at kinabukasan.',
    },
    ceb: {
      questionText: 'Sa Jeremias 29:11, unsang matanga sa mga hunahuna ang gipahayag sa Ginoo alang sa Iyang katawhan?',
      options: [
        'Mga hunahuna sa kalinaw ug dili sa kadaotan, aron sa paghatag kanila ug paglaom ug kaugmaon',
        'Mga hunahuna sa walay kataposang pagkabihag sa Babilonia',
        'Mga hunahuna sa pakig-alyansa kang Faraon',
        'Mga hunahuna sa pagbiya sa Jerusalem sa hingpit',
      ],
      correctAnswer: 'Mga hunahuna sa kalinaw ug dili sa kadaotan, aron sa paghatag kanila ug paglaom ug kaugmaon',
      explanation: 'Ang Jeremias 29:11 nag-ingon nga ang mga hunahuna sa Ginoo mao ang kalinaw aron sa paghatag ug paglaom ug kaugmaon.',
    },
  }),
  makeFull66SeedItem({
    questionCode: 'QB-66-LAM-001',
    category: 'Biblical Teachings',
    bibleBook: 'Lamentations',
    testament: 'Old Testament',
    difficulty: 'Easy',
    scriptureReference: 'Lamentations 3:22-23',
    en: {
      questionText: 'According to Lamentations 3:22-23, why are we not consumed, and how often are Yahweh\'s compassions renewed?',
      options: [
        'Because of Yahweh\'s loving kindnesses; they are new every morning',
        'Because of the walls of Jerusalem; they are rebuilt every decade',
        'Because of the strength of horses; they are renewed every winter',
        'Because of royal decrees; they are proclaimed every sabbath year',
      ],
      correctAnswer: 'Because of Yahweh\'s loving kindnesses; they are new every morning',
      explanation: 'Lamentations 3:22-23 (WEB) declares: "It is because of Yahweh\'s loving kindnesses that we are not consumed, because his compassion doesn\'t fail. They are new every morning. Great is your faithfulness."',
    },
    tl: {
      questionText: 'Ayon sa Mga Panaghoy 3:22-23, bakit hindi tayo nalilipol at gaano kadalas nagiging bago ang habag ng Panginoon?',
      options: [
        'Dahil sa kagandahang-loob ng Panginoon; ang mga ito ay bago tuwing umaga',
        'Dahil sa pader ng Jerusalem; itinatayo ito tuwing sampung taon',
        'Dahil sa lakas ng mga kabayo; nagbabago ito tuwing taglamig',
        'Dahil sa utos ng hari; inihahayag ito tuwing ikapitong taon',
      ],
      correctAnswer: 'Dahil sa kagandahang-loob ng Panginoon; ang mga ito ay bago tuwing umaga',
      explanation: 'Sinasabi sa Mga Panaghoy 3:22-23 na ang Kanyang mga habag ay hindi nagmamaliw at bago tuwing umaga; dakila ang Kanyang katapatan.',
    },
    ceb: {
      questionText: 'Sumala sa Mga Pagbakho 3:22-23, nganong wala kita mangaut-ot ug kapila mabag-o ang kaluoy sa Ginoo?',
      options: [
        'Tungod sa mahigugmaong kalulot sa Ginoo; kini bag-o sa matag buntag',
        'Tungod sa paril sa Jerusalem; gitukod kini matag napulo ka tuig',
        'Tungod sa kusog sa mga kabayo; nabag-o kini matag tingtugnaw',
        'Tungod sa sugo sa hari; gimantala kini matag ikapitong tuig',
      ],
      correctAnswer: 'Tungod sa mahigugmaong kalulot sa Ginoo; kini bag-o sa matag buntag',
      explanation: 'Ang Mga Pagbakho 3:22-23 nagpahayag nga ang kaluoy sa Ginoo bag-o sa matag buntag ug dako ang Iyang pagkamatinumanon.',
    },
  }),
  makeFull66SeedItem({
    questionCode: 'QB-66-EZE-001',
    category: 'Prophecies and Fulfillments',
    bibleBook: 'Ezekiel',
    testament: 'Old Testament',
    difficulty: 'Easy',
    scriptureReference: 'Ezekiel 37:3',
    en: {
      questionText: 'In the valley of dry bones in Ezekiel 37:3, what question did the Lord ask the prophet Ezekiel?',
      options: [
        '"Son of man, can these bones live?"',
        '"Son of man, how high is the wall of Tyre?"',
        '"Son of man, who built the ships of Tarshish?"',
        '"Son of man, how many stars are in the sky?"',
      ],
      correctAnswer: '"Son of man, can these bones live?"',
      explanation: 'Ezekiel 37:3 (WEB) states: "He said to me, \'Son of man, can these bones live?\' I answered, \'Lord Yahweh, you know.\'"',
    },
    tl: {
      questionText: 'Sa libis ng mga tuyong buto sa Ezekiel 37:3, anong tanong ang itinanong ng Panginoon kay propeta Ezekiel?',
      options: [
        '"Anak ng tao, mabubuhay pa kaya ang mga butong ito?"',
        '"Anak ng tao, gaano kataas ang pader ng Tiro?"',
        '"Anak ng tao, sino ang gumawa ng mga barko ng Tarsis?"',
        '"Anak ng tao, ilan ang mga bituin sa langit?"',
      ],
      correctAnswer: '"Anak ng tao, mabubuhay pa kaya ang mga butong ito?"',
      explanation: 'Sa Ezekiel 37:3, tinanong ng Panginoon si Ezekiel: "Anak ng tao, mabubuhay pa kaya ang mga butong ito?"',
    },
    ceb: {
      questionText: 'Sa walog sa mga ugang bukog sa Ezequiel 37:3, unsay gipangutana sa Ginoo kang propeta Ezequiel?',
      options: [
        '"Anak sa tawo, mabuhi pa ba kining mga bukoga?"',
        '"Anak sa tawo, unsa kahabog ang paril sa Tiro?"',
        '"Anak sa tawo, kinsay naghimo sa mga barko sa Tarsis?"',
        '"Anak sa tawo, pila ang mga bituon sa langit?"',
      ],
      correctAnswer: '"Anak sa tawo, mabuhi pa ba kining mga bukoga?"',
      explanation: 'Ang Ezequiel 37:3 nagtala sa pangutana sa Ginoo: "Anak sa tawo, mabuhi pa ba kining mga bukoga?"',
    },
  }),
  makeFull66SeedItem({
    questionCode: 'QB-66-HOS-001',
    category: 'Prophecies and Fulfillments',
    bibleBook: 'Hosea',
    testament: 'Old Testament',
    difficulty: 'Intermediate',
    scriptureReference: 'Hosea 6:6',
    en: {
      questionText: 'In Hosea 6:6, what does God say He desires rather than sacrifice and burnt offerings?',
      options: [
        'Mercy and the knowledge of God',
        'Gold vessels and cedar altars',
        'Fasting for forty days every month',
        'Conquest of the Philistine cities',
      ],
      correctAnswer: 'Mercy and the knowledge of God',
      explanation: 'Hosea 6:6 (WEB) declares: "For I desire mercy, and not sacrifice; and the knowledge of God more than burnt offerings."',
    },
    tl: {
      questionText: 'Sa Oseas 6:6, ano ang sinabi ng Diyos na Kanyang kinalulugdan nang higit kaysa sa hain at mga handog na sinusunog?',
      options: [
        'Habag (awa) at ang pagkakilala sa Diyos',
        'Mga sisidlang ginto at dambanang sedro',
        'Pag-aayuno nang apatnapung araw bawat buwan',
        'Pagsakop sa mga lungsod ng Filistia',
      ],
      correctAnswer: 'Habag (awa) at ang pagkakilala sa Diyos',
      explanation: 'Sinasabi sa Oseas 6:6: "Sapagkat habag ang aking nais, at hindi hain; at ang pagkakilala sa Diyos higit kaysa mga handog na sinusunog."',
    },
    ceb: {
      questionText: 'Sa Oseas 6:6, unsay giingon sa Diyos nga Iyang gitinguha labaw pa kay sa halad ug mga halad nga sinunog?',
      options: [
        'Kaluoy ug ang kahibalo mahitungod sa Diyos',
        'Bulawan nga mga sudlanan ug halaran nga sedro',
        'Pagpuasa sulod sa kap-atan ka adlaw matag bulan',
        'Pagsakop sa mga siyudad sa Filistia',
      ],
      correctAnswer: 'Kaluoy ug ang kahibalo mahitungod sa Diyos',
      explanation: 'Ang Oseas 6:6 nag-ingon: "Kay ako nagtinguha sa kaluoy, ug dili sa halad; ug sa kahibalo sa Diyos labaw pa kay sa mga halad nga sinunog."',
    },
  }),
  makeFull66SeedItem({
    questionCode: 'QB-66-JOE-001',
    category: 'Prophecies and Fulfillments',
    bibleBook: 'Joel',
    testament: 'Old Testament',
    difficulty: 'Easy',
    scriptureReference: 'Joel 2:28',
    en: {
      questionText: 'What prophecy does Yahweh proclaim in Joel 2:28 that Peter later quotes at Pentecost in Acts 2?',
      options: [
        '"I will pour out my Spirit on all flesh; and your sons and your daughters will prophesy"',
        '"I will rebuild the walls of Jericho with precious stones"',
        '"I will divide the Red Sea into twelve paths"',
        '"I will send manna upon Mount Hermon for a thousand years"',
      ],
      correctAnswer: '"I will pour out my Spirit on all flesh; and your sons and your daughters will prophesy"',
      explanation: 'Joel 2:28 (WEB) states: "It will happen afterward, that I will pour out my Spirit on all flesh; and your sons and your daughters will prophesy."',
    },
    tl: {
      questionText: 'Anong propesiya ang ipinahayag sa Joel 2:28 na binanggit din ni Pedro noong araw ng Pentecostes sa Mga Gawa 2?',
      options: [
        '"Ibubuhos ko ang aking Espiritu sa lahat ng laman; at ang inyong mga anak na lalaki at babae ay magpapahayag ng propesiya"',
        '"Muling itatayo ang pader ng Jerico gamit ang mahahalagang bato"',
        '"Hahatiin ang Dagat na Pula sa labindalawang landas"',
        '"Magpapadala ng manna sa Bundok Hermon sa loob ng sanlibong taon"',
      ],
      correctAnswer: '"Ibubuhos ko ang aking Espiritu sa lahat ng laman; at ang inyong mga anak na lalaki at babae ay magpapahayag ng propesiya"',
      explanation: 'Ayon sa Joel 2:28, ibubuhos ng Diyos ang Kanyang Espiritu sa lahat ng laman at ang kanilang mga anak ay magpopropesiya.',
    },
    ceb: {
      questionText: 'Unsang tagna sa Joel 2:28 ang gikutlo usab ni Pedro sa adlaw sa Pentecostes sa Mga Buhat 2?',
      options: [
        '"Ibubo ko ang akong Espiritu sa tanang unod; ug ang inyong mga anak nga lalaki ug babaye managna"',
        '"Itukod pag-usab ang paril sa Jerico pinaagi sa mahalong mga bato"',
        '"Bahinon ang Pulang Dagat sa napulo ug duha ka dalan"',
        '"Magpadala ug manna sa Bukid sa Hermon sulod sa usa ka libo ka tuig"',
      ],
      correctAnswer: '"Ibubo ko ang akong Espiritu sa tanang unod; ug ang inyong mga anak nga lalaki ug babaye managna"',
      explanation: 'Ang Joel 2:28 nagtagna nga ibubo sa Diyos ang Iyang Espiritu sa tanang unod ug ang ilang mga anak managna.',
    },
  }),
  makeFull66SeedItem({
    questionCode: 'QB-66-AMO-001',
    category: 'Prophecies and Fulfillments',
    bibleBook: 'Amos',
    testament: 'Old Testament',
    difficulty: 'Intermediate',
    scriptureReference: 'Amos 5:24',
    en: {
      questionText: 'In Amos 5:24, what two things does the prophet call to roll on "like rivers" and "like a mighty stream"?',
      options: [
        'Justice and righteousness',
        'Gold and frankincense',
        'Chariots and horses',
        'Wine and olive oil',
      ],
      correctAnswer: 'Justice and righteousness',
      explanation: 'Amos 5:24 (WEB) proclaims: "But let justice roll on like rivers, and righteousness like a mighty stream."',
    },
    tl: {
      questionText: 'Sa Amos 5:24, anong dalawang bagay ang ipinananawagan ng propeta na umagos na "parang tubig" at "parang malakas na batis"?',
      options: [
        'Katarungan at katuwiran',
        'Ginto at kamanyang',
        'Mga karwahe at kabayo',
        'Alak at langis ng olibo',
      ],
      correctAnswer: 'Katarungan at katuwiran',
      explanation: 'Sinasabi sa Amos 5:24: "Kundi padaluyin ang katarungan na parang tubig, at ang katuwiran na parang malakas na batis."',
    },
    ceb: {
      questionText: 'Sa Amos 5:24, unsang duha ka butang ang giawhag sa propeta nga ipabaha "sama sa tubig" ug "sama sa kusog nga sapa"?',
      options: [
        'Ang hustisya ug pagkamatarong',
        'Bulawan ug insenso',
        'Mga karwahe ug kabayo',
        'Bino ug lana sa olibo',
      ],
      correctAnswer: 'Ang hustisya ug pagkamatarong',
      explanation: 'Ang Amos 5:24 nag-ingon: "Apan paagasa ang hustisya sama sa tubig, ug ang pagkamatarong sama sa kusog nga sapa."',
    },
  }),
  makeFull66SeedItem({
    questionCode: 'QB-66-OBA-001',
    category: 'Prophecies and Fulfillments',
    bibleBook: 'Obadiah',
    testament: 'Old Testament',
    difficulty: 'Advanced',
    scriptureReference: 'Obadiah 1:4',
    en: {
      questionText: 'Against which proud nation—descended from Esau and dwelling in the clefts of the rock—is the prophecy of Obadiah 1:4 directed?',
      options: ['Edom', 'Assyria', 'Moab', 'Philistia'],
      correctAnswer: 'Edom',
      explanation: 'Obadiah\'s vision is concerning Edom, warning in Obadiah 1:4 (WEB): "Though you mount on high as the eagle, and though your nest is set among the stars, I will bring you down from there, says Yahweh."',
    },
    tl: {
      questionText: 'Laban sa aling palalong bansa—na nagmula kay Esau at naninirahan sa mga bitak ng malalaking bato—ipinahayag ang propesiya sa Obadias 1:4?',
      options: ['Edom', 'Asiria', 'Moab', 'Filistia'],
      correctAnswer: 'Edom',
      explanation: 'Ang pangitain ni Obadias ay laban sa Edom (Obadias 1:1-4), na nagbabala na bagaman itayo nila ang kanilang pugad na kasingtaas ng agila ay ibababa sila ng Panginoon.',
    },
    ceb: {
      questionText: 'Batok sa unsang mapahitas-ong nasod—nga kaliwat ni Esau ug nagpuyo sa mga liki sa bato—ang tagna sa Abdias (Obadiah) 1:4?',
      options: ['Edom', 'Asiria', 'Moab', 'Filistia'],
      correctAnswer: 'Edom',
      explanation: 'Ang panan-awon ni Abdias (Obadiah 1:1-4) gitumong batok sa Edom tungod sa ilang garbo.',
    },
  }),
  makeFull66SeedItem({
    questionCode: 'QB-66-MIC-001',
    category: 'Biblical Teachings',
    bibleBook: 'Micah',
    testament: 'Old Testament',
    difficulty: 'Easy',
    scriptureReference: 'Micah 6:8',
    en: {
      questionText: 'According to Micah 6:8, what three things does Yahweh require of man?',
      options: [
        'To act justly, to love mercy, and to walk humbly with your God',
        'To offer thousands of rams and ten thousands of rivers of oil',
        'To build fortresses in Judah and ships in Ezion Geber',
        'To fast every Monday and wear garments of fine linen',
      ],
      correctAnswer: 'To act justly, to love mercy, and to walk humbly with your God',
      explanation: 'Micah 6:8 (WEB) states: "He has shown you, O man, what is good. What does Yahweh require of you, but to act justly, to love mercy, and to walk humbly with your God?"',
    },
    tl: {
      questionText: 'Ayon sa Mikas 6:8, anong tatlong bagay ang hinihingi ng Panginoon sa tao?',
      options: [
        'Gumawa nang may katarungan, ibigin angawa, at lumakad nang may kapakumbabaan na kasama ng iyong Diyos',
        'Maghandog ng libu-libong tupang lalaki at ilog ng langis',
        'Magtayo ng mga kuta sa Juda at mga barko sa Ezion-geber',
        'Mag-ayuno tuwing Lunes at magsuot ng mainam na lino',
      ],
      correctAnswer: 'Gumawa nang may katarungan, ibigin angawa, at lumakad nang may kapakumbabaan na kasama ng iyong Diyos',
      explanation: 'Sinasabi sa Mikas 6:8 na ang hinihingi ng Panginoon ay gumawa ng katarungan, ibigin ang kaawaan, at lumakad na may kapakumbabaan kasama ng Diyos.',
    },
    ceb: {
      questionText: 'Sumala sa Miqueas 6:8, unsang tulo ka butang ang gikinahanglan sa Ginoo gikan sa tawo?',
      options: [
        'Ang pagbuhat sa hustisya, paghigugma sa kaluoy, ug paglakaw nga mapaubsanon uban sa imong Diyos',
        'Ang paghalad ug linibo ka laking karnero ug mga suba sa lana',
        'Ang pagtukod ug mga kuta sa Juda ug mga barko',
        'Ang pagpuasa matag Lunes ug pagsul-ob ug lino',
      ],
      correctAnswer: 'Ang pagbuhat sa hustisya, paghigugma sa kaluoy, ug paglakaw nga mapaubsanon uban sa imong Diyos',
      explanation: 'Ang Miqueas 6:8 nag-ingon nga ang gikinahanglan sa Ginoo mao ang pagbuhat sa matarong, paghigugma sa kaluoy, ug paglakaw nga mapaubsanon uban sa Diyos.',
    },
  }),
  makeFull66SeedItem({
    questionCode: 'QB-66-NAH-001',
    category: 'Prophecies and Fulfillments',
    bibleBook: 'Nahum',
    testament: 'Old Testament',
    difficulty: 'Intermediate',
    scriptureReference: 'Nahum 1:7',
    en: {
      questionText: 'What comforting truth does Nahum 1:7 declare about Yahweh in the day of trouble?',
      options: [
        'Yahweh is good, a stronghold in the day of trouble; and he knows those who take refuge in him',
        'Nineveh will stand forever as an undefeated empire',
        'The rivers of Assyria will never overflow their banks',
        'Chariots of iron are stronger than the mountain fortresses',
      ],
      correctAnswer: 'Yahweh is good, a stronghold in the day of trouble; and he knows those who take refuge in him',
      explanation: 'Nahum 1:7 (WEB) states: "Yahweh is good, a stronghold in the day of trouble; and he knows those who take refuge in him."',
    },
    tl: {
      questionText: 'Anong katotohanan ang ipinahayag ng Nahum 1:7 tungkol sa Panginoon sa araw ng kabagabagan?',
      options: [
        'Ang Panginoon ay mabuti, isang matibay na kanlungan sa araw ng kabagabagan, at kilala Niya ang mga nanganganlong sa Kanya',
        'Ang Nineve ay mananatili magpakailanman bilang imperyo',
        'Ang mga ilog ng Asiria ay hindi kailanman aapaw',
        'Ang mga karwaheng bakal ay mas matibay kaysa sa bundok',
      ],
      correctAnswer: 'Ang Panginoon ay mabuti, isang matibay na kanlungan sa araw ng kabagabagan, at kilala Niya ang mga nanganganlong sa Kanya',
      explanation: 'Sinasabi sa Nahum 1:7 na ang Panginoon ay mabuti, isang matibay na kuta sa araw ng kabagabagan, at nakikilala Niya ang mga nagtitiwala sa Kanya.',
    },
    ceb: {
      questionText: 'Unsang makapahupay nga kamatuoran ang gipahayag sa Nahum 1:7 mahitungod sa Ginoo sa adlaw sa kasamok?',
      options: [
        'Ang Ginoo maayo, usa ka salipdanan sa adlaw sa kasamok; ug Siya nakaila kanila nga modangop Kaniya',
        'Ang Nineve mobarog hangtod sa kahangtoran',
        'Ang mga suba sa Asiria dili gayod mobaha',
        'Ang mga karwahe nga puthaw mas lig-on pa kay sa kabukiran',
      ],
      correctAnswer: 'Ang Ginoo maayo, usa ka salipdanan sa adlaw sa kasamok; ug Siya nakaila kanila nga modangop Kaniya',
      explanation: 'Ang Nahum 1:7 nag-ingon nga ang Ginoo maayo, usa ka kuta sa adlaw sa kalisdanan, ug nakaila Siya sa mga modangop Kaniya.',
    },
  }),
  makeFull66SeedItem({
    questionCode: 'QB-66-HAB-001',
    category: 'Prophecies and Fulfillments',
    bibleBook: 'Habakkuk',
    testament: 'Old Testament',
    difficulty: 'Intermediate',
    scriptureReference: 'Habakkuk 2:4',
    en: {
      questionText: 'What foundational statement in Habakkuk 2:4 is later quoted in Romans 1:17, Galatians 3:11, and Hebrews 10:38?',
      options: [
        '"The righteous will live by his faith"',
        '"The swift will always win the battle"',
        '"The Chaldeans will rule for ten generations"',
        '"The watchtower of Jerusalem will never fall"',
      ],
      correctAnswer: '"The righteous will live by his faith"',
      explanation: 'Habakkuk 2:4 (WEB) declares: "Behold, his soul is puffed up. It is not upright in him, but the righteous will live by his faith."',
    },
    tl: {
      questionText: 'Anong mahalagang pahayag sa Habacuc 2:4 ang sinipi rin sa Roma 1:17, Galacia 3:11, at Hebreo 10:38?',
      options: [
        '"Ang matuwid ay mabubuhay sa pamamagitan ng kanyang pananampalataya"',
        '"Ang matulin ay laging magwawagi sa digmaan"',
        '"Ang mga Caldeo ay maghahari sa sampung salinlahi"',
        '"Ang bantayan ng Jerusalem ay hindi babagsak"',
      ],
      correctAnswer: '"Ang matuwid ay mabubuhay sa pamamagitan ng kanyang pananampalataya"',
      explanation: 'Ipinahayag sa Habacuc 2:4 na "ang matuwid ay mabubuhay sa pamamagitan ng kanyang pananampalataya."',
    },
    ceb: {
      questionText: 'Unsang mahinungdanong pahayag sa Habacuc 2:4 ang gikutlo usab sa Roma 1:17, Galacia 3:11, ug Hebreohanon 10:38?',
      options: [
        '"Ang matarong mabuhi pinaagi sa iyang pagtuo"',
        '"Ang matulin kanunay nga modaog sa gubat"',
        '"Ang mga Caldeanhon maghari sulod sa napulo ka kaliwatan"',
        '"Ang bantayanan sa Jerusalem dili mapukan"',
      ],
      correctAnswer: '"Ang matarong mabuhi pinaagi sa iyang pagtuo"',
      explanation: 'Ang Habacuc 2:4 nagpahayag: "ang matarong mabuhi pinaagi sa iyang pagtuo."',
    },
  }),
  makeFull66SeedItem({
    questionCode: 'QB-66-ZEP-001',
    category: 'Prophecies and Fulfillments',
    bibleBook: 'Zephaniah',
    testament: 'Old Testament',
    difficulty: 'Intermediate',
    scriptureReference: 'Zephaniah 3:17',
    en: {
      questionText: 'In Zephaniah 3:17, how is Yahweh described as responding to His people in their midst?',
      options: [
        'A mighty one who will save, who will rejoice over them with joy and singing',
        'A distant judge who refuses to hear any prayer',
        'A merchant weighing silver in the gates of Gaza',
        'A traveler passing quickly through the wilderness',
      ],
      correctAnswer: 'A mighty one who will save, who will rejoice over them with joy and singing',
      explanation: 'Zephaniah 3:17 (WEB) says: "Yahweh, your God, is among you, a mighty one who will save. He will rejoice over you with joy. He will calm you in his love. He will rejoice over you with singing."',
    },
    tl: {
      questionText: 'Sa Zefanias 3:17, paano inilarawan ang Panginoong Diyos na nasa kalagitnaan ng Kanyang bayan?',
      options: [
        'Isang makapangyarihang magliligtas na magagalak sa kanila nang may kagalakan at awitan',
        'Isang malayong hukom na hindi nakikinig sa panalangin',
        'Isang mangangalakal na nagtitimbang ng pilak sa Gaza',
        'Isang manlalakbay na dumaraan lamang sa ilang',
      ],
      correctAnswer: 'Isang makapangyarihang magliligtas na magagalak sa kanila nang may kagalakan at awitan',
      explanation: 'Ayon sa Zefanias 3:17, ang Panginoong Diyos ay nasa gitna nila bilang makapangyarihang Tagapagligtas na nagagalak sa kanila nang may awitan.',
    },
    ceb: {
      questionText: 'Sa Sofonias 3:17, giunsa paghulagway ang Ginoong Diyos nga anaa sa taliwala sa Iyang katawhan?',
      options: [
        'Usa ka gamhanan nga magaluwas, nga magmaya tungod kanila uban ang kalipay ug pag-awit',
        'Usa ka halayo nga maghuhukom nga dili maminaw sa pag-ampo',
        'Usa ka magpapatigayon nga nagtimbang ug pilak sa Gaza',
        'Usa ka magpapanaw nga miagi lamang sa kamingawan',
      ],
      correctAnswer: 'Usa ka gamhanan nga magaluwas, nga magmaya tungod kanila uban ang kalipay ug pag-awit',
      explanation: 'Ang Sofonias 3:17 nag-ingon nga ang Ginoo anaa sa ilang taliwala, usa ka gamhanan nga magaluwas ug magmaya kanila uban ang pag-awit.',
    },
  }),
  makeFull66SeedItem({
    questionCode: 'QB-66-HAG-001',
    category: 'Bible History',
    bibleBook: 'Haggai',
    testament: 'Old Testament',
    difficulty: 'Intermediate',
    scriptureReference: 'Haggai 2:9',
    en: {
      questionText: 'In Haggai 2:9, what did Yahweh of Armies promise regarding the rebuilt Temple in Jerusalem?',
      options: [
        'The latter glory of this house will be greater than the former, and in this place He will give peace',
        'The temple would be moved from Jerusalem to Samaria',
        'No sacrifices would ever be offered there again',
        'Its walls would be constructed entirely of Egyptian marble',
      ],
      correctAnswer: 'The latter glory of this house will be greater than the former, and in this place He will give peace',
      explanation: 'Haggai 2:9 (WEB) declares: "The latter glory of this house will be greater than the former, says Yahweh of Armies; and in this place I will give peace."',
    },
    tl: {
      questionText: 'Sa Hagai 2:9, ano ang ipinangako ng Panginoon ng mga Hukbo tungkol sa muling itinayong Templo sa Jerusalem?',
      options: [
        'Ang huling kaluwalhatian ng bahay na ito ay magiging higit kaysa sa una, at sa dakong ito ay magbibigay Siya ng kapayapaan',
        'Ang templo ay ililipat mula sa Jerusalem patungong Samaria',
        'Wala nang ihahandog na hain doon kailanman',
        'Ang mga pader nito ay gagawaing marmol mula sa Ehipto',
      ],
      correctAnswer: 'Ang huling kaluwalhatian ng bahay na ito ay magiging higit kaysa sa una, at sa dakong ito ay magbibigay Siya ng kapayapaan',
      explanation: 'Sinasabi sa Hagai 2:9 na ang huling kaluwalhatian ng bahay na ito ay magiging mas dakila kaysa sa una.',
    },
    ceb: {
      questionText: 'Sa Hageo 2:9, unsay gisaad sa Ginoo sa mga Panon mahitungod sa gitukod pag-usab nga Templo sa Jerusalem?',
      options: [
        'Ang ulahing himaya niining balaya mahimong labaw pa kay sa nahauna, ug niining dapita Siya mohatag ug kalinaw',
        'Ang templo ibalhin gikan sa Jerusalem ngadto sa Samaria',
        'Wala nay mga halad nga ihalad didto',
        'Ang mga paril niini himoong marmol gikan sa Ehipto',
      ],
      correctAnswer: 'Ang ulahing himaya niining balaya mahimong labaw pa kay sa nahauna, ug niining dapita Siya mohatag ug kalinaw',
      explanation: 'Ang Hageo 2:9 nagpahayag nga ang ulahing himaya niining balaya mahimong labaw pa kay sa nahauna.',
    },
  }),
  makeFull66SeedItem({
    questionCode: 'QB-66-ZEC-001',
    category: 'Prophecies and Fulfillments',
    bibleBook: 'Zechariah',
    testament: 'Old Testament',
    difficulty: 'Easy',
    scriptureReference: 'Zechariah 4:6',
    en: {
      questionText: 'What famous message did the angel give to Zerubbabel in Zechariah 4:6?',
      options: [
        '"Not by might, nor by power, but by my Spirit, says Yahweh of Armies"',
        '"By the sword and by the chariot you shall conquer Babylon"',
        '"Gather silver and gold from Sidon to hire soldiers"',
        '"Wait seventy more years before laying the foundation"',
      ],
      correctAnswer: '"Not by might, nor by power, but by my Spirit, says Yahweh of Armies"',
      explanation: 'Zechariah 4:6 (WEB) states: "This is Yahweh\'s word to Zerubbabel, saying, \'Not by might, nor by power, but by my Spirit, says Yahweh of Armies.\'"',
    },
    tl: {
      questionText: 'Anong tanyag na mensahe ng Panginoon ang ibinigay kay Zerubabel sa Zacarias 4:6?',
      options: [
        '"Hindi sa pamamagitan ng kalakasan, ni sa kapangyarihan, kundi sa pamamagitan ng aking Espiritu, sabi ng Panginoon ng mga Hukbo"',
        '"Sa pamamagitan ng tabak at karwahe ay sasakupin mo ang Babilonia"',
        '"Magtipon ng pilak at ginto mula sa Sidon upang umupa ng mga kawal"',
        '"Maghintay pa ng pitumpung taon bago ilatag ang pundasyon"',
      ],
      correctAnswer: '"Hindi sa pamamagitan ng kalakasan, ni sa kapangyarihan, kundi sa pamamagitan ng aking Espiritu, sabi ng Panginoon ng mga Hukbo"',
      explanation: 'Sinasabi sa Zacarias 4:6: "Hindi sa pamamagitan ng kalakasan, ni sa kapangyarihan, kundi sa pamamagitan ng aking Espiritu."',
    },
    ceb: {
      questionText: 'Unsang iladong mensahe sa Ginoo ang gihatag kang Zorobabel sa Zacarias 4:6?',
      options: [
        '"Dili pinaagi sa kusog, ni pinaagi sa gahom, kondili pinaagi sa akong Espiritu, nag-ingon ang Ginoo sa mga Panon"',
        '"Pinaagi sa espada ug karwahe imong sakopon ang Babilonia"',
        '"Pagtigom ug pilak ug bulawan gikan sa Sidon"',
        '"Paghulat pa ug kapitoan ka tuig sa dili pa magtukod"',
      ],
      correctAnswer: '"Dili pinaagi sa kusog, ni pinaagi sa gahom, kondili pinaagi sa akong Espiritu, nag-ingon ang Ginoo sa mga Panon"',
      explanation: 'Ang Zacarias 4:6 nag-ingon: "Dili pinaagi sa kusog, ni pinaagi sa gahom, kondili pinaagi sa akong Espiritu."',
    },
  }),
  makeFull66SeedItem({
    questionCode: 'QB-66-MAL-001',
    category: 'Prophecies and Fulfillments',
    bibleBook: 'Malachi',
    testament: 'Old Testament',
    difficulty: 'Easy',
    scriptureReference: 'Malachi 3:10',
    en: {
      questionText: 'In Malachi 3:10, what does Yahweh of Armies instruct the people to bring into the storehouse so that there may be food in His house?',
      options: ['The whole tithe', 'A golden crown', 'Cedar timber from Lebanon', 'Water from the Jordan'],
      correctAnswer: 'The whole tithe',
      explanation: 'Malachi 3:10 (WEB) states: "Bring the whole tithe into the storehouse, that there may be food in my house, and test me now in this, says Yahweh of Armies."',
    },
    tl: {
      questionText: 'Sa Malakias 3:10, ano ang iniutos ng Panginoon ng mga Hukbo na dalhin sa kamalig upang magkaroon ng pagkain sa Kanyang bahay?',
      options: ['Ang buong ikapu', 'Isang gintong korona', 'Kahoy na sedro mula sa Libano', 'Tubig mula sa Jordan'],
      correctAnswer: 'Ang buong ikapu',
      explanation: 'Sinasabi sa Malakias 3:10 na dalhin ang buong ikapu sa kamalig upang magkaroon ng pagkain sa bahay ng Diyos.',
    },
    ceb: {
      questionText: 'Sa Malaquias 3:10, unsay gisugo sa Ginoo sa mga Panon nga dad-on ngadto sa buhisanan aron adunay pagkaon sa Iyang balay?',
      options: ['Ang tibuok nga ikapulo', 'Usa ka bulawan nga korona', 'Kahoy nga sedro gikan sa Libano', 'Tubig gikan sa Jordan'],
      correctAnswer: 'Ang tibuok nga ikapulo',
      explanation: 'Ang Malaquias 3:10 nagsugo sa pagdala sa tibuok nga ikapulo ngadto sa tipiganan.',
    },
  }),
  makeFull66SeedItem({
    questionCode: 'QB-66-MRK-001',
    category: 'New Testament',
    bibleBook: 'Mark',
    testament: 'New Testament',
    difficulty: 'Easy',
    scriptureReference: 'Mark 10:45',
    en: {
      questionText: 'According to Mark 10:45, for what purpose did the Son of Man come?',
      options: [
        'Not to be served, but to serve, and to give his life as a ransom for many',
        'To build an earthly palace in Jerusalem and tax the nations',
        'To appoint twelve Roman governors over Galilee',
        'To abolish the reading of the Psalms in the synagogues',
      ],
      correctAnswer: 'Not to be served, but to serve, and to give his life as a ransom for many',
      explanation: 'Mark 10:45 (WEB) states: "For even the Son of Man came not to be served, but to serve, and to give his life as a ransom for many."',
    },
    tl: {
      questionText: 'Ayon sa Marcos 10:45, para sa anong layunin naparito ang Anak ng Tao?',
      options: [
        'Hindi upang paglingkuran, kundi upang maglingkod at ibigay ang Kanyang buhay bilang pantubos sa marami',
        'Upang magtayo ng palasyo sa Jerusalem at maningil ng buwis',
        'Upang magtalaga ng labindalawang gobernador sa Galilea',
        'Upang ipatigil ang pagbabasa ng Mga Awit',
      ],
      correctAnswer: 'Hindi upang paglingkuran, kundi upang maglingkod at ibigay ang Kanyang buhay bilang pantubos sa marami',
      explanation: 'Sinabi ni Hesus sa Marcos 10:45 na ang Anak ng Tao ay naparito hindi upang paglingkuran kundi upang maglingkod at ibigay ang Kanyang buhay na pantubos sa marami.',
    },
    ceb: {
      questionText: 'Sumala sa Marcos 10:45, alang sa unsang katuyoan mianhi ang Anak sa Tawo?',
      options: [
        'Dili aron alagaran, kondili sa pag-alagad ug sa paghatag sa Iyang kinabuhi ingon nga lukat alang sa daghan',
        'Aron magtukod ug palasyo sa Jerusalem',
        'Aron magtudlo ug napulo ug duha ka gobernador sa Galilea',
        'Aron hunongon ang pagbasa sa Mga Salmo',
      ],
      correctAnswer: 'Dili aron alagaran, kondili sa pag-alagad ug sa paghatag sa Iyang kinabuhi ingon nga lukat alang sa daghan',
      explanation: 'Ang Marcos 10:45 nag-ingon nga ang Anak sa Tawo mianhi sa pag-alagad ug paghatag sa Iyang kinabuhi ingon nga lukat sa daghan.',
    },
  }),
  makeFull66SeedItem({
    questionCode: 'QB-66-2CO-001',
    category: 'Biblical Teachings',
    bibleBook: '2 Corinthians',
    testament: 'New Testament',
    difficulty: 'Easy',
    scriptureReference: '2 Corinthians 5:17',
    en: {
      questionText: 'According to 2 Corinthians 5:17, what is true of anyone who is in Christ?',
      options: [
        'He is a new creation; the old things have passed away and all things have become new',
        'He must travel to Corinth once a year to offer sacrifices',
        'He will never experience any earthly trials',
        'He is required to speak every language of the Roman Empire',
      ],
      correctAnswer: 'He is a new creation; the old things have passed away and all things have become new',
      explanation: '2 Corinthians 5:17 (WEB) declares: "Therefore if anyone is in Christ, he is a new creation. The old things have passed away. Behold, all things have become new."',
    },
    tl: {
      questionText: 'Ayon sa 2 Mga Taga-Corinto 5:17, ano ang totoo sa sinumang nakay Kristo?',
      options: [
        'Siya ay isang bagong nilalang; ang mga dating bagay ay lumipas na at ang lahat ay naging bago',
        'Kailangan niyang maglakbay sa Corinto taun-taon',
        'Hindi na siya daranas ng anumang pagsubok sa lupa',
        'Kailangan niyang magsalita ng lahat ng wika sa Roma',
      ],
      correctAnswer: 'Siya ay isang bagong nilalang; ang mga dating bagay ay lumipas na at ang lahat ay naging bago',
      explanation: 'Sinasabi sa 2 Mga Taga-Corinto 5:17 na kung ang sinuman ay nakay Kristo, siya ay bagong nilalang.',
    },
    ceb: {
      questionText: 'Sumala sa 2 Mga Taga-Corinto 5:17, unsa ang mahitabo kon ang usa ka tawo anaa kang Kristo?',
      options: [
        'Siya usa na ka bag-ong binuhat; ang daang mga butang nangagi na ug ang tanan nahimong bag-o',
        'Kinahanglan siyang mobiyahe sa Corinto matag tuig',
        'Dili na siya makasinati ug pagsulay sa kalibotan',
        'Kinahanglan siyang mosulti sa tanang pinulongan sa Roma',
      ],
      correctAnswer: 'Siya usa na ka bag-ong binuhat; ang daang mga butang nangagi na ug ang tanan nahimong bag-o',
      explanation: 'Ang 2 Mga Taga-Corinto 5:17 nag-ingon nga kon ang bisan kinsa anaa kang Kristo, siya bag-o na nga binuhat.',
    },
  }),
  makeFull66SeedItem({
    questionCode: 'QB-66-COL-001',
    category: 'Biblical Teachings',
    bibleBook: 'Colossians',
    testament: 'New Testament',
    difficulty: 'Easy',
    scriptureReference: 'Colossians 3:23',
    en: {
      questionText: 'How does Colossians 3:23 instruct believers to approach whatever work they do?',
      options: [
        'Work heartily, as for the Lord, and not for men',
        'Work only when watched by earthly rulers',
        'Avoid all manual labor and live in solitude',
        'Seek praise from the merchants of Colosse',
      ],
      correctAnswer: 'Work heartily, as for the Lord, and not for men',
      explanation: 'Colossians 3:23 (WEB) instructs: "And whatever you do, work heartily, as for the Lord, and not for men."',
    },
    tl: {
      questionText: 'Paano tinuturuan ng Mga Taga-Colosas 3:23 ang mga mananampalataya sa anumang kanilang ginagawa?',
      options: [
        'Gawin ito nang buong puso na gaya ng para sa Panginoon at hindi para sa tao',
        'Magtrabaho lamang kapag nakatingin ang mga pinuno',
        'Iwasan ang lahat ng gawain at mamuhay nang mag-isa',
        'Hangarin ang papuri ng mga mangangalakal sa Colosas',
      ],
      correctAnswer: 'Gawin ito nang buong puso na gaya ng para sa Panginoon at hindi para sa tao',
      explanation: 'Sinasabi sa Mga Taga-Colosas 3:23: "Anomang inyong ginagawa, ay inyong gawin ng buong puso, na gaya ng sa Panginoon, at hindi sa mga tao."',
    },
    ceb: {
      questionText: 'Giunsa pagtudlo sa Mga Taga-Colosas 3:23 ang mga magtutuo sa bisan unsa nga ilang buhaton?',
      options: [
        'Buhata kini sa kinasingkasing ingon nga alang sa Ginoo ug dili alang sa mga tawo',
        'Pagtrabaho lamang kon nagtan-aw ang mga punoan',
        'Likayi ang tanang buhat ug pagpuyo nga nag-inusara',
        'Pangitaa ang pagdayeg sa mga magpapatigayon sa Colosas',
      ],
      correctAnswer: 'Buhata kini sa kinasingkasing ingon nga alang sa Ginoo ug dili alang sa mga tawo',
      explanation: 'Ang Mga Taga-Colosas 3:23 nagtudlo nga buhaton ang tanan sa kinasingkasing ingon nga alang sa Ginoo.',
    },
  }),
  makeFull66SeedItem({
    questionCode: 'QB-66-1TH-001',
    category: 'Biblical Teachings',
    bibleBook: '1 Thessalonians',
    testament: 'New Testament',
    difficulty: 'Easy',
    scriptureReference: '1 Thessalonians 5:16-18',
    en: {
      questionText: 'What three commands are given together in 1 Thessalonians 5:16-18 as the will of God in Christ Jesus?',
      options: [
        'Always rejoice, pray without ceasing, and in everything give thanks',
        'Sell your fields, move to Macedonia, and build a synagogue',
        'Fast for seven days, wear sackcloth, and sound the trumpet',
        'Debate the philosophers, gather gold, and sail to Rome',
      ],
      correctAnswer: 'Always rejoice, pray without ceasing, and in everything give thanks',
      explanation: '1 Thessalonians 5:16-18 (WEB) states: "Always rejoice. Pray without ceasing. In everything give thanks, for this is the will of God in Christ Jesus toward you."',
    },
    tl: {
      questionText: 'Anong tatlong utos ang magkakasamang ibinigay sa 1 Mga Taga-Tesalonica 5:16-18 bilang kalooban ng Diyos kay Kristo Hesus?',
      options: [
        'Magalak kayong lagi, manalangin nang walang patid, at sa lahat ng bagay ay magpasalamat',
        'Ipagbili ang inyong bukid, lumipat sa Macedonia, at magtayo ng sinagoga',
        'Mag-ayuno nang pitong araw, magsuot ng sako, at hipan ang trumpeta',
        'Makipagtalo sa mga pilosopo, magtipon ng ginto, at maglayag sa Roma',
      ],
      correctAnswer: 'Magalak kayong lagi, manalangin nang walang patid, at sa lahat ng bagay ay magpasalamat',
      explanation: 'Nakasaad sa 1 Mga Taga-Tesalonica 5:16-18: "Magalak kayong lagi. Manalangin kayong walang patid. Sa lahat ng mga bagay ay magpasalamat kayo."',
    },
    ceb: {
      questionText: 'Unsang tulo ka sugo ang gihatag sa 1 Mga Taga-Tesalonica 5:16-18 ingon nga kabubut-on sa Diyos diha kang Kristo Hesus?',
      options: [
        'Paglipay kamo kanunay, pag-ampo sa walay hunong, ug sa tanang butang pagpasalamat',
        'Ibaligya ang inyong uma, balhin sa Macedonia, ug pagtukod ug sinagoga',
        'Pagpuasa sulod sa pito ka adlaw ug huypa ang trumpeta',
        'Pakiglantugi sa mga pilosopo ug paglawig paingon sa Roma',
      ],
      correctAnswer: 'Paglipay kamo kanunay, pag-ampo sa walay hunong, ug sa tanang butang pagpasalamat',
      explanation: 'Ang 1 Mga Taga-Tesalonica 5:16-18 nagsugo sa paglipay kanunay, pag-ampo sa walay hunong, ug pagpasalamat sa tanang butang.',
    },
  }),
  makeFull66SeedItem({
    questionCode: 'QB-66-2TH-001',
    category: 'Biblical Teachings',
    bibleBook: '2 Thessalonians',
    testament: 'New Testament',
    difficulty: 'Intermediate',
    scriptureReference: '2 Thessalonians 3:3',
    en: {
      questionText: 'In 2 Thessalonians 3:3, what does Paul assure the believers that the faithful Lord will do for them?',
      options: [
        'Establish them and guard them from the evil one',
        'Exempt them from all daily work in Thessalonica',
        'Give them political rule over Macedonia',
        'Send angels with chariots of gold to Corinth',
      ],
      correctAnswer: 'Establish them and guard them from the evil one',
      explanation: '2 Thessalonians 3:3 (WEB) declares: "But the Lord is faithful, who will establish you and guard you from the evil one."',
    },
    tl: {
      questionText: 'Sa 2 Mga Taga-Tesalonica 3:3, ano ang tiniyak ni Pablo na gagawin ng tapat na Panginoon para sa mga mananampalataya?',
      options: [
        'Patitibayin sila at iingatan sila laban sa masama',
        'Palalayain sila sa lahat ng pang-araw-araw na gawain',
        'Bibigyan sila ng kapangyarihang pulitikal sa Macedonia',
        'Magpapadala ng mga gintong karwahe sa Corinto',
      ],
      correctAnswer: 'Patitibayin sila at iingatan sila laban sa masama',
      explanation: 'Sinasabi sa 2 Mga Taga-Tesalonica 3:3: "Ngunit tapat ang Panginoon, na magpapatibay sa inyo at mag-iingat sa inyo laban sa masama."',
    },
    ceb: {
      questionText: 'Sa 2 Mga Taga-Tesalonica 3:3, unsay gipasalig ni Pablo nga buhaton sa matinumanong Ginoo alang sa mga magtutuo?',
      options: [
        'Maglig-on kanila ug magbantay kanila gikan sa daotan',
        'Magpahigawas kanila sa tanang adlaw-adlaw nga buhat',
        'Mohatag kanila ug gahom sa politika sa Macedonia',
        'Magpadala ug bulawan nga mga karwahe sa Corinto',
      ],
      correctAnswer: 'Maglig-on kanila ug magbantay kanila gikan sa daotan',
      explanation: 'Ang 2 Mga Taga-Tesalonica 3:3 nag-ingon: "Apan ang Ginoo matinumanon, nga maglig-on kaninyo ug magbantay kaninyo gikan sa daotan."',
    },
  }),
  makeFull66SeedItem({
    questionCode: 'QB-66-1TI-001',
    category: 'Biblical Teachings',
    bibleBook: '1 Timothy',
    testament: 'New Testament',
    difficulty: 'Easy',
    scriptureReference: '1 Timothy 4:12',
    en: {
      questionText: 'In 1 Timothy 4:12, Paul tells Timothy, "Let no man despise your youth," and urges him to be an example to believers in what areas?',
      options: [
        'In word, in way of life, in love, in spirit, in faith, and in purity',
        'In wealth, in Roman Eloquence, in military rank, and in lineage',
        'In building temples, collecting taxes, and ruling cities',
        'In fasting forty days and living in the desert',
      ],
      correctAnswer: 'In word, in way of life, in love, in spirit, in faith, and in purity',
      explanation: '1 Timothy 4:12 (WEB) states: "Let no man despise your youth; but be an example to those who believe, in word, in your way of life, in love, in spirit, in faith, and in purity."',
    },
    tl: {
      questionText: 'Sa 1 Timoteo 4:12, sinabi ni Pablo kay Timoteo na huwag hamakin ng sinuman ang kanyang kabataan, kundi maging halimbawa sa mga mananampalataya sa anong mga bagay?',
      options: [
        'Sa pananalita, sa pamumuhay, sa pag-ibig, sa espiritu, sa pananampalataya, at sa kalinisan',
        'Sa kayamanan, sa pilosopiya ng Roma, at sa antas ng lipunan',
        'Sa pagtatayo ng mga templo at paniningil ng buwis',
        'Sa pag-aayuno nang apatnapung araw sa ilang',
      ],
      correctAnswer: 'Sa pananalita, sa pamumuhay, sa pag-ibig, sa espiritu, sa pananampalataya, at sa kalinisan',
      explanation: 'Ayon sa 1 Timoteo 4:12, dapat maging halimbawa si Timoteo sa pananalita, sa ugali, sa pag-ibig, sa pananampalataya, at sa kalinisan.',
    },
    ceb: {
      questionText: 'Sa 1 Timoteo 4:12, gitambagan ni Pablo si Timoteo nga dili tamayon ang iyang kabatan-on, kondili mahimong panig-ingnan sa mga magtutuo sa unsang mga butang?',
      options: [
        'Sa pulong, sa panggawi, sa gugma, sa espiritu, sa pagtuo, ug sa kaputli',
        'Sa bahandi, sa kaalam sa Roma, ug sa katungdanan',
        'Sa pagtukod ug mga templo ug pagpaningil ug buhis',
        'Sa pagpuasa sulod sa kap-atan ka adlaw sa kamingawan',
      ],
      correctAnswer: 'Sa pulong, sa panggawi, sa gugma, sa espiritu, sa pagtuo, ug sa kaputli',
      explanation: 'Ang 1 Timoteo 4:12 nag-awhag nga mahimong panig-ingnan sa pulong, sa panggawi, sa gugma, sa pagtuo, ug sa kaputli.',
    },
  }),
  makeFull66SeedItem({
    questionCode: 'QB-66-TIT-001',
    category: 'Biblical Teachings',
    bibleBook: 'Titus',
    testament: 'New Testament',
    difficulty: 'Intermediate',
    scriptureReference: 'Titus 3:5',
    en: {
      questionText: 'According to Titus 3:5, on what basis did God save us, rather than by works of righteousness which we did ourselves?',
      options: [
        'According to His mercy, through the washing of regeneration and renewing by the Holy Spirit',
        'According to the number of sacrifices offered in Crete',
        'According to our earthly lineage and philosophical wisdom',
        'According to our observance of Roman feast days',
      ],
      correctAnswer: 'According to His mercy, through the washing of regeneration and renewing by the Holy Spirit',
      explanation: 'Titus 3:5 (WEB) states: "Not by works of righteousness which we did ourselves, but according to his mercy, he saved us, through the washing of regeneration and renewing by the Holy Spirit."',
    },
    tl: {
      questionText: 'Ayon sa Tito 3:5, sa anong batayan tayo iniligtas ng Diyos, at hindi dahil sa mga gawa ng katuwiran na ating ginawa?',
      options: [
        'Ayon sa Kanyang habag, sa pamamagitan ng paghuhugas ng muling kapanganakan at pagbabago sa pamamagitan ng Banal na Espiritu',
        'Ayon sa dami ng mga handog na inialay sa Creta',
        'Ayon sa ating angkan at karunungan sa pilosopiya',
        'Ayon sa pagsunod sa mga kapistahan ng Roma',
      ],
      correctAnswer: 'Ayon sa Kanyang habag, sa pamamagitan ng paghuhugas ng muling kapanganakan at pagbabago sa pamamagitan ng Banal na Espiritu',
      explanation: 'Sinasabi sa Tito 3:5 na iniligtas Niya tayo ayon sa Kanyang kaawaan, sa pamamagitan ng paghuhugas ng muling kapanganakan at ng pagbabago ng Banal na Espiritu.',
    },
    ceb: {
      questionText: 'Sumala sa Tito 3:5, sa unsang paagi kita giluwas sa Diyos, ug dili tungod sa mga buhat sa pagkamatarong nga atong nahimo?',
      options: [
        'Sumala sa Iyang kaluoy, pinaagi sa paghugas sa pagkatawo pag-usab ug pagbag-o sa Espiritu Santo',
        'Sumala sa gidaghanon sa mga halad didto sa Creta',
        'Sumala sa atong kaliwat ug kaalam sa pilosopiya',
        'Sumala sa pagsaulog sa mga pista sa Roma',
      ],
      correctAnswer: 'Sumala sa Iyang kaluoy, pinaagi sa paghugas sa pagkatawo pag-usab ug pagbag-o sa Espiritu Santo',
      explanation: 'Ang Tito 3:5 nag-ingon nga kita giluwas Niya sumala sa Iyang kaluoy pinaagi sa paghugas sa pagkatawo pag-usab ug pagbag-o sa Espiritu Santo.',
    },
  }),
  makeFull66SeedItem({
    questionCode: 'QB-66-PHM-001',
    category: 'Bible Characters',
    bibleBook: 'Philemon',
    testament: 'New Testament',
    difficulty: 'Easy',
    scriptureReference: 'Philemon 1:10',
    en: {
      questionText: 'In Philemon 1:10, on behalf of which runaway servant—who became a spiritual son to Paul during his imprisonment—does Paul appeal to Philemon?',
      options: ['Onesimus', 'Tychicus', 'Epaphras', 'Demas'],
      correctAnswer: 'Onesimus',
      explanation: 'Philemon 1:10 (WEB) states: "I appeal to you for my child Onesimus, whom I have become the father of in my chains."',
    },
    tl: {
      questionText: 'Sa Filemon 1:10, para kaninong lingkod—na naging anak sa pananampalataya ni Pablo habang siya ay nakabilanggo—pakiusap na namamagitan si Pablo kay Filemon?',
      options: ['Onesimo', 'Tiquico', 'Epafras', 'Demas'],
      correctAnswer: 'Onesimo',
      explanation: 'Sa Filemon 1:10, nakikiusap si Pablo kay Filemon para kay Onesimo na naging kanyang anak sa pananampalataya habang siya ay nakatanikala.',
    },
    ceb: {
      questionText: 'Sa Filemon 1:10, alang kang kinsang sulugoon—nga nahimong espirituhanong anak ni Pablo samtang binilanggo siya—mihangyo si Pablo kang Filemon?',
      options: ['Onesimo', 'Tiquico', 'Epafras', 'Demas'],
      correctAnswer: 'Onesimo',
      explanation: 'Ang Filemon 1:10 nagtala sa hangyo ni Pablo alang kang Onesimo nga iyang anak diha sa iyang mga gapos.',
    },
  }),
  makeFull66SeedItem({
    questionCode: 'QB-66-JAS-001',
    category: 'Biblical Teachings',
    bibleBook: 'James',
    testament: 'New Testament',
    difficulty: 'Easy',
    scriptureReference: 'James 1:22',
    en: {
      questionText: 'What exhortation does James 1:22 give to believers regarding the word of God?',
      options: [
        'Be doers of the word, and not only hearers, deluding your own selves',
        'Hear the word once a year and keep silent',
        'Write the word only on golden tablets',
        'Debate the word in the marketplaces for silver',
      ],
      correctAnswer: 'Be doers of the word, and not only hearers, deluding your own selves',
      explanation: 'James 1:22 (WEB) instructs: "But be doers of the word, and not only hearers, deluding your own selves."',
    },
    tl: {
      questionText: 'Anong payo ang ibinibigay ng Santiago 1:22 sa mga mananampalataya tungkol sa salita ng Diyos?',
      options: [
        'Maging tagatupad kayo ng salita, at huwag tagapakinig lamang na dinadaya ang inyong sarili',
        'Pakinggan lamang ang salita minsan sa isang taon',
        'Isulat lamang ang salita sa mga tapyas na ginto',
        'Pagtalunan ang salita sa pamilihan kapalit ng pilak',
      ],
      correctAnswer: 'Maging tagatupad kayo ng salita, at huwag tagapakinig lamang na dinadaya ang inyong sarili',
      explanation: 'Sinasabi sa Santiago 1:22: "Datapuwa\'t maging tagatupad kayo ng salita, at huwag tagapakinig lamang."',
    },
    ceb: {
      questionText: 'Unsang tambag ang gihatag sa Santiago 1:22 ngadto sa mga magtutuo mahitungod sa pulong sa Diyos?',
      options: [
        'Magmamando ug magbubuhat kamo sa pulong, ug dili tigpaminaw lamang nga naglimbong sa inyong kaugalingon',
        'Paminawa lamang ang pulong kausa sa usa ka tuig',
        'Isulat lamang ang pulong sa bulawan nga mga tabla',
        'Lantugia ang pulong sa merkado alang sa pilak',
      ],
      correctAnswer: 'Magmamando ug magbubuhat kamo sa pulong, ug dili tigpaminaw lamang nga naglimbong sa inyong kaugalingon',
      explanation: 'Ang Santiago 1:22 nagtudlo nga mahimong magbubuhat sa pulong ug dili tigpaminaw lamang.',
    },
  }),
  makeFull66SeedItem({
    questionCode: 'QB-66-1PE-001',
    category: 'Biblical Teachings',
    bibleBook: '1 Peter',
    testament: 'New Testament',
    difficulty: 'Easy',
    scriptureReference: '1 Peter 5:7',
    en: {
      questionText: 'Why does 1 Peter 5:7 encourage believers to cast all their worries and anxieties on God?',
      options: [
        'Because He cares for you',
        'Because earthly kings will pay your debts',
        'Because angels will build your houses',
        'Because trials last only one hour',
      ],
      correctAnswer: 'Because He cares for you',
      explanation: '1 Peter 5:7 (WEB) states: "Casting all your worries on him, because he cares for you."',
    },
    tl: {
      questionText: 'Bakit hinihikayat ng 1 Pedro 5:7 ang mga mananampalataya na ihagis ang lahat ng kanilang kabalisahan sa Diyos?',
      options: [
        'Sapagkat Siya ay nagmamalasakit sa inyo',
        'Sapagkat babayaran ng mga hari sa lupa ang inyong utang',
        'Sapagkat itatayo ng mga anghel ang inyong bahay',
        'Sapagkat ang pagsubok ay tumatagal lamang ng isang oras',
      ],
      correctAnswer: 'Sapagkat Siya ay nagmamalasakit sa inyo',
      explanation: 'Sinasabi sa 1 Pedro 5:7: "Na ilagak ninyo sa kaniya ang lahat ng inyong kabalisahan, sapagkat siya ay nagmamalasakit sa inyo."',
    },
    ceb: {
      questionText: 'Nganong giawhag sa 1 Pedro 5:7 ang mga magtutuo sa pagtugyan sa tanan nilang kabalaka ngadto sa Diyos?',
      options: [
        'Tungod kay Siya nagmahal ug nagtagad kaninyo',
        'Tungod kay ang mga hari sa yuta mobayad sa inyong utang',
        'Tungod kay ang mga anghel magtukod sa inyong balay',
        'Tungod kay ang pagsulay molungtad lamang ug usa ka oras',
      ],
      correctAnswer: 'Tungod kay Siya nagmahal ug nagtagad kaninyo',
      explanation: 'Ang 1 Pedro 5:7 nag-ingon: "Itugyan ngadto kaniya ang tanan ninyong kabalaka, kay siya nagtagad kaninyo."',
    },
  }),
  makeFull66SeedItem({
    questionCode: 'QB-66-2PE-001',
    category: 'Biblical Teachings',
    bibleBook: '2 Peter',
    testament: 'New Testament',
    difficulty: 'Intermediate',
    scriptureReference: '2 Peter 3:9',
    en: {
      questionText: 'According to 2 Peter 3:9, why is the Lord patient with us concerning His promise?',
      options: [
        'Not wishing that any should perish, but that all should come to repentance',
        'Waiting for the Roman Empire to build more roads',
        'Allowing the false teachers to gather more silver',
        'Waiting until the temple in Jerusalem is rebuilt seven times',
      ],
      correctAnswer: 'Not wishing that any should perish, but that all should come to repentance',
      explanation: '2 Peter 3:9 (WEB) states: "The Lord is not slow concerning his promise, as some count slowness; but is patient with us, not wishing that any should perish, but that all should come to repentance."',
    },
    tl: {
      questionText: 'Ayon sa 2 Pedro 3:9, bakit mapagpahinuhod at matiyaga ang Panginoon sa atin tungkol sa Kanyang pangako?',
      options: [
        'Ayaw Niyang mapahamak ang sinuman, kundi ang lahat ay dumating sa pagsisisi',
        'Hinihintay Niyang makapagtayo ng mas maraming kalsada ang Roma',
        'Hinahayaan Niyang makapagtipon ng pilak ang mga bulaang guro',
        'Hinihintay Niyang maitayo nang pitong ulit ang templo',
      ],
      correctAnswer: 'Ayaw Niyang mapahamak ang sinuman, kundi ang lahat ay dumating sa pagsisisi',
      explanation: 'Nakasaad sa 2 Pedro 3:9 na ang Panginoon ay matiyaga sa atin, na hindi Niya ibig na sinuman ay mapahamak kundi ang lahat ay magsisi.',
    },
    ceb: {
      questionText: 'Sumala sa 2 Pedro 3:9, nganong mapailobon ang Ginoo kanato mahitungod sa Iyang saad?',
      options: [
        'Dili Siya buot nga adunay malaglag, kondili nga ang tanan makadangat sa paghinulsol',
        'Naghulat Siya nga ang Imperyo sa Roma makahimo ug dugang dalan',
        'Gitugotan Niya ang mga mini nga magtutudlo sa pagtigom ug pilak',
        'Naghulat Siya nga matukod sa makapito ang templo',
      ],
      correctAnswer: 'Dili Siya buot nga adunay malaglag, kondili nga ang tanan makadangat sa paghinulsol',
      explanation: 'Ang 2 Pedro 3:9 nag-ingon nga ang Ginoo mapailobon kanato ug dili buot nga adunay malaglag kondili nga ang tanan maghinulsol.',
    },
  }),
  makeFull66SeedItem({
    questionCode: 'QB-66-1JN-001',
    category: 'Biblical Teachings',
    bibleBook: '1 John',
    testament: 'New Testament',
    difficulty: 'Easy',
    scriptureReference: '1 John 4:8',
    en: {
      questionText: 'What profound truth about God\'s nature is declared in 1 John 4:8?',
      options: [
        'He who doesn\'t love doesn\'t know God, for God is love',
        'God is known only through philosophical debate',
        'Only those who dwell in Jerusalem can know God',
        'Love is secondary to wealth and status',
      ],
      correctAnswer: 'He who doesn\'t love doesn\'t know God, for God is love',
      explanation: '1 John 4:8 (WEB) declares: "He who doesn\'t love doesn\'t know God, for God is love."',
    },
    tl: {
      questionText: 'Anong dakilang katotohanan tungkol sa kalikasan ng Diyos ang ipinahayag sa 1 Juan 4:8?',
      options: [
        'Ang hindi umiibig ay hindi nakakakilala sa Diyos, sapagkat ang Diyos ay pag-ibig',
        'Ang Diyos ay nakikilala lamang sa pakikipagtalo',
        'Tanging ang mga nakatira sa Jerusalem ang nakakakilala sa Diyos',
        'Ang pag-ibig ay pangalawa lamang sa kayamanan',
      ],
      correctAnswer: 'Ang hindi umiibig ay hindi nakakakilala sa Diyos, sapagkat ang Diyos ay pag-ibig',
      explanation: 'Sinasabi sa 1 Juan 4:8: "Ang hindi umiibig ay hindi nakakakilala sa Diyos; sapagkat ang Diyos ay pag-ibig."',
    },
    ceb: {
      questionText: 'Unsang dakong kamatuoran mahitungod sa kinaiya sa Diyos ang gipahayag sa 1 Juan 4:8?',
      options: [
        'Ang wala mahigugma wala makaila sa Diyos, kay ang Diyos gugma',
        'Ang Diyos mailhan lamang pinaagi sa lantugi sa pilosopiya',
        'Ang nagpuyo lamang sa Jerusalem ang makaila sa Diyos',
        'Ang gugma ikaduha lamang sa bahandi',
      ],
      correctAnswer: 'Ang wala mahigugma wala makaila sa Diyos, kay ang Diyos gugma',
      explanation: 'Ang 1 Juan 4:8 nagpahayag: "Ang wala mahigugma wala makaila sa Diyos; kay ang Diyos gugma man."',
    },
  }),
  makeFull66SeedItem({
    questionCode: 'QB-66-2JN-001',
    category: 'Biblical Teachings',
    bibleBook: '2 John',
    testament: 'New Testament',
    difficulty: 'Intermediate',
    scriptureReference: '2 John 1:6',
    en: {
      questionText: 'How does the apostle John define love in 2 John 1:6?',
      options: [
        'This is love, that we should walk according to His commandments',
        'That we should gather wealth for the elders of Ephesus',
        'That we should welcomingly host every false teacher',
        'That we should abandon the written Scriptures',
      ],
      correctAnswer: 'This is love, that we should walk according to His commandments',
      explanation: '2 John 1:6 (WEB) states: "This is love, that we should walk according to his commandments. This is the commandment, even as you heard from the beginning, that you should walk in it."',
    },
    tl: {
      questionText: 'Paano inilarawan ni apostol Juan ang pag-ibig sa 2 Juan 1:6?',
      options: [
        'Ito ang pag-ibig, na tayo ay lumakad ayon sa Kanyang mga utos',
        'Na tayo ay magtipon ng kayamanan para sa Efeso',
        'Na tanggapin natin ang bawat bulaang guro',
        'Na iwanan natin ang nasusulat na Kasulatan',
      ],
      correctAnswer: 'Ito ang pag-ibig, na tayo ay lumakad ayon sa Kanyang mga utos',
      explanation: 'Sinasabi sa 2 Juan 1:6: "At ito ang pag-ibig, na tayo\'y mangagsilakad ayon sa kaniyang mga utos."',
    },
    ceb: {
      questionText: 'Giunsa paghubit ni apostol Juan ang gugma sa 2 Juan 1:6?',
      options: [
        'Mao kini ang gugma, nga kita maglakaw sumala sa Iyang mga sugo',
        'Nga kita magtigom ug bahandi alang sa Efeso',
        'Nga atong dawaton ang matag mini nga magtutudlo',
        'Nga atong biyaan ang sinulat nga Kasulatan',
      ],
      correctAnswer: 'Mao kini ang gugma, nga kita maglakaw sumala sa Iyang mga sugo',
      explanation: 'Ang 2 Juan 1:6 nag-ingon: "Ug mao kini ang gugma, nga kita managgawi sumala sa iyang mga sugo."',
    },
  }),
  makeFull66SeedItem({
    questionCode: 'QB-66-3JN-001',
    category: 'Bible Characters',
    bibleBook: '3 John',
    testament: 'New Testament',
    difficulty: 'Intermediate',
    scriptureReference: '3 John 1:11',
    en: {
      questionText: 'In 3 John 1:11, what exhortation does John give to his beloved friend Gaius?',
      options: [
        'Don\'t imitate that which is evil, but that which is good. He who does good is of God',
        'Imitate Diotrephes and refuse to welcome the brothers',
        'Leave the church and sail immediately to Patmos',
        'Stop supporting traveling missionaries of the Gospel',
      ],
      correctAnswer: 'Don\'t imitate that which is evil, but that which is good. He who does good is of God',
      explanation: '3 John 1:11 (WEB) states: "Beloved, don\'t imitate that which is evil, but that which is good. He who does good is of God. He who does evil hasn\'t seen God."',
    },
    tl: {
      questionText: 'Sa 3 Juan 1:11, anong payo ang ibinigay ni Juan sa kanyang minamahal na kaibigang si Gayo?',
      options: [
        'Huwag mong tularan ang masama, kundi ang mabuti. Ang gumagawa ng mabuti ay sa Diyos',
        'Tularan mo si Diotrefes at huwag tanggapin ang mga kapatid',
        'Umalis ka sa iglesya at maglayag agad patungong Patmos',
        'Itigil ang pagtulong sa mga misyonero ng Ebanghelyo',
      ],
      correctAnswer: 'Huwag mong tularan ang masama, kundi ang mabuti. Ang gumagawa ng mabuti ay sa Diyos',
      explanation: 'Sinasabi sa 3 Juan 1:11: "Minamahal, huwag mong tularan ang masama, kundi ang mabuti. Ang gumagawa ng mabuti ay sa Diyos."',
    },
    ceb: {
      questionText: 'Sa 3 Juan 1:11, unsang tambag ang gihatag ni Juan sa iyang hinigugmang higala nga si Gayo?',
      options: [
        'Ayaw pagsundog sa daotan, kondili sa maayo. Ang nagbuhat ug maayo iya sa Diyos',
        'Sundoga si Diotrefes ug ayaw dawata ang mga igsoon',
        'Biya sa iglesia ug paglawig dayon ngadto sa Patmos',
        'Hunonga ang pagtabang sa mga misyonaryo sa Maayong Balita',
      ],
      correctAnswer: 'Ayaw pagsundog sa daotan, kondili sa maayo. Ang nagbuhat ug maayo iya sa Diyos',
      explanation: 'Ang 3 Juan 1:11 nag-ingon: "Hinigugma, ayaw pagsundog sa daotan, kondili sa maayo. Ang nagbuhat ug maayo iya sa Diyos."',
    },
  }),
  makeFull66SeedItem({
    questionCode: 'QB-66-JUD-001',
    category: 'Advanced Biblical Studies',
    bibleBook: 'Jude',
    testament: 'New Testament',
    difficulty: 'Intermediate',
    scriptureReference: 'Jude 1:3',
    en: {
      questionText: 'In Jude 1:3, why did Jude write to the believers regarding their common salvation?',
      options: [
        'To exhort them to contend earnestly for the faith which was once for all delivered to the saints',
        'To instruct them to abandon the Old Testament prophecies',
        'To announce a new earthly kingdom in Galilee',
        'To collect tribute for the Roman governor',
      ],
      correctAnswer: 'To exhort them to contend earnestly for the faith which was once for all delivered to the saints',
      explanation: 'Jude 1:3 (WEB) states: "I was constrained to write to you exhorting you to contend earnestly for the faith which was once for all delivered to the saints."',
    },
    tl: {
      questionText: 'Sa Judas 1:3, bakit sumulat si Judas sa mga mananampalataya tungkol sa kanilang karaniwang kaligtasan?',
      options: [
        'Upang hikayatin silang makipaglabang masikap para sa pananampalataya na ibinigay na minsan at magpakailanman sa mga banal',
        'Upang utusan silang iwan ang mga propesiya sa Lumang Tipan',
        'Upang ipahayag ang bagong kaharian sa Galilea',
        'Upang mangolekta ng buwis para sa gobernador ng Roma',
      ],
      correctAnswer: 'Upang hikayatin silang makipaglabang masikap para sa pananampalataya na ibinigay na minsan at magpakailanman sa mga banal',
      explanation: 'Nakasaad sa Judas 1:3 na sumulat siya upang hikayatin silang ipaglaban nang masikap ang pananampalatayang ipinagkatiwala sa mga banal.',
    },
    ceb: {
      questionText: 'Sa Judas 1:3, nganong nagsulat si Judas ngadto sa mga magtutuo mahitungod sa ilang komon nga kaluwasan?',
      options: [
        'Sa pagdasig kanila sa pagpakigbisog sa makanunayon alang sa pagtuo nga sa makausa gitugyan ngadto sa mga balaan',
        'Sa pagsugo kanila sa pagbiya sa mga tagna sa Daang Tugon',
        'Sa pagpahibalo sa bag-ong gingharian sa Galilea',
        'Sa pagpaningil ug buhis alang sa gobernador sa Roma',
      ],
      correctAnswer: 'Sa pagdasig kanila sa pagpakigbisog sa makanunayon alang sa pagtuo nga sa makausa gitugyan ngadto sa mga balaan',
      explanation: 'Ang Judas 1:3 nag-awhag sa mga magtutuo sa pagpakigbisog alang sa pagtuo nga gitugyan ngadto sa mga balaan.',
    },
  }),
  makeFull66SeedItem({
    questionCode: 'QB-66-LEV-001',
    category: 'Biblical Teachings',
    bibleBook: 'Leviticus',
    testament: 'Old Testament',
    difficulty: 'Easy',
    scriptureReference: 'Leviticus 19:18',
    en: {
      questionText: 'What famous commandment regarding one\'s neighbor is recorded in Leviticus 19:18?',
      options: [
        'You shall love your neighbor as yourself',
        'You shall build a stone wall between your neighbor\'s field and yours',
        'You shall lend silver only to royal officials',
        'You shall greet your neighbor only on the Sabbath',
      ],
      correctAnswer: 'You shall love your neighbor as yourself',
      explanation: 'Leviticus 19:18 (WEB) commands: "You shall not take vengeance, nor bear any grudge against the children of your people; but you shall love your neighbor as yourself. I am Yahweh."',
    },
    tl: {
      questionText: 'Anong tanyag na utos tungkol sa kapwa ang nakatala sa Levitico 19:18?',
      options: [
        'Iibigin mo ang iyong kapwa na gaya ng iyong sarili',
        'Magtatayo ka ng pader na bato sa pagitan ng inyong bukid',
        'Magpapahiram ka lamang ng pilak sa mga pinuno',
        'Babatiin mo lamang ang iyong kapwa tuwing Sabbath',
      ],
      correctAnswer: 'Iibigin mo ang iyong kapwa na gaya ng iyong sarili',
      explanation: 'Sinasabi sa Levitico 19:18: "Iibigin mo ang iyong kapwa na gaya ng iyong sarili: ako ang Panginoon."',
    },
    ceb: {
      questionText: 'Unsang iladong sugo mahitungod sa silingan ang nahisulat sa Levitico 19:18?',
      options: [
        'Higugmaa ang imong silingan sama sa imong kaugalingon',
        'Pagtukod ug paril nga bato tali sa inyong uma',
        'Pagpahulam ug pilak ngadto lamang sa mga opisyal',
        'imbaha lamang ang imong silingan sa adlaw nga Igpapahulay',
      ],
      correctAnswer: 'Higugmaa ang imong silingan sama sa imong kaugalingon',
      explanation: 'Ang Levitico 19:18 nagsugo: "Higugmaa ang imong silingan sama sa imong kaugalingon: Ako mao ang Ginoo."',
    },
  }),
  makeFull66SeedItem({
    questionCode: 'QB-66-JOS-001',
    category: 'Bible Events',
    bibleBook: 'Joshua',
    testament: 'Old Testament',
    difficulty: 'Easy',
    scriptureReference: 'Joshua 1:9',
    en: {
      questionText: 'What encouragement did Yahweh give to Joshua in Joshua 1:9 as he prepared to lead Israel into the Promised Land?',
      options: [
        'Be strong and courageous; don\'t be afraid, for Yahweh your God is with you wherever you go',
        'Wait forty more years in the wilderness before crossing the Jordan',
        'Send messengers to Egypt to request chariots and horsemen',
        'Build a fortress on Mount Nebo and remain there',
      ],
      correctAnswer: 'Be strong and courageous; don\'t be afraid, for Yahweh your God is with you wherever you go',
      explanation: 'Joshua 1:9 (WEB) states: "Haven\'t I commanded you? Be strong and courageous. Don\'t be afraid. Don\'t be dismayed, for Yahweh your God is with you wherever you go."',
    },
    tl: {
      questionText: 'Anong pampatibay-loob ang ibinigay ng Panginoon kay Josue sa Josue 1:9 bago sila pumasok sa Lupang Pangako?',
      options: [
        'Magpakalakas ka at magpakatapang; huwag kang matakot sapagkat ang Panginoon mong Diyos ay kasama mo saan ka man pumunta',
        'Maghintay pa ng apatnapung taon sa ilang bago tumawid sa Jordan',
        'Magpadala ng mga sugo sa Ehipto upang humingi ng mga karwahe',
        'Magtayo ng kuta sa Bundok Nebo at manatili roon',
      ],
      correctAnswer: 'Magpakalakas ka at magpakatapang; huwag kang matakot sapagkat ang Panginoon mong Diyos ay kasama mo saan ka man pumunta',
      explanation: 'Nakasaad sa Josue 1:9: "Magpakalakas ka at magpakatapang na mabuti; huwag kang matakot, ni manglupaypay: sapagkat ang Panginoon mong Diyos ay sumasaiyo saan ka man pumaroon."',
    },
    ceb: {
      questionText: 'Unsang pagdasig ang gihatag sa Ginoo kang Josue sa Josue 1:9 sa dihang mangulo na siya sa Israel?',
      options: [
        'Pagmakusganon ug pagmaisogon; ayaw kahadlok, kay ang Ginoo nga imong Diyos mag-uban kanimo bisan asa ka moadto',
        'Paghulat pa ug kap-atan ka tuig sa kamingawan',
        'Pagpadala ug mga sinugo ngadto sa Ehipto aron mangayo ug karwahe',
        'Pagtukod ug kuta sa Bukid sa Nebo',
      ],
      correctAnswer: 'Pagmakusganon ug pagmaisogon; ayaw kahadlok, kay ang Ginoo nga imong Diyos mag-uban kanimo bisan asa ka moadto',
      explanation: 'Ang Josue 1:9 nag-ingon: "Pagmakusganon ug pagmaisogon; ayaw kahadlok... kay ang Ginoo nga imong Diyos mag-uban kanimo bisan asa ka paingon."',
    },
  }),
  makeFull66SeedItem({
    questionCode: 'QB-66-ISA-001',
    category: 'Prophecies and Fulfillments',
    bibleBook: 'Isaiah',
    testament: 'Old Testament',
    difficulty: 'Easy',
    scriptureReference: 'Isaiah 9:6',
    en: {
      questionText: 'In the messianic prophecy of Isaiah 9:6, what four titles are given to the child who is born and son who is given?',
      options: [
        'Wonderful Counselor, Mighty God, Everlasting Father, Prince of Peace',
        'King of Babylon, Ruler of Tyre, Judge of Moab, Shield of Damascus',
        'Prophet of Sinai, Scribe of Judah, Watchman of Ephraim, Shepherd of Gilead',
        'Captain of Chariots, Master of Gold, Builder of Walls, Keeper of Gates',
      ],
      correctAnswer: 'Wonderful Counselor, Mighty God, Everlasting Father, Prince of Peace',
      explanation: 'Isaiah 9:6 (WEB) declares: "For to us a child is born. To us a son is given; and the government will be on his shoulders. His name will be called Wonderful Counselor, Mighty God, Everlasting Father, Prince of Peace."',
    },
    tl: {
      questionText: 'Sa propesiya ng Isaias 9:6, anong apat na pangalan o titulo ang itatawag sa batang ipinanganak at anak na ibinigay sa atin?',
      options: [
        'Kahanga-hangang Tagapayo, Makapangyarihang Diyos, Walang Hanggang Ama, Prinsipe ng Kapayapaan',
        'Hari ng Babilonia, Pinuno ng Tiro, Hukom ng Moab, Kalasag ng Damasco',
        'Propeta ng Sinai, Eskriba ng Juda, Bantay ng Efraim, Pastol ng Galaad',
        'Pinuno ng mga Karwahe, Panginoon ng Ginto, Tagapagtayo ng Pader, Bantay ng Pintuan',
      ],
      correctAnswer: 'Kahanga-hangang Tagapayo, Makapangyarihang Diyos, Walang Hanggang Ama, Prinsipe ng Kapayapaan',
      explanation: 'Sinasabi sa Isaias 9:6 na ang Kanyang pangalan ay tatawaging Kahanga-hangang Tagapayo, Makapangyarihang Diyos, Walang Hanggang Ama, Prinsipe ng Kapayapaan.',
    },
    ceb: {
      questionText: 'Sa tagna sa Isaias 9:6, unsang upat ka titulo ang itawag sa bata nga natawo ug anak nga gihatag kanato?',
      options: [
        'Katingalahang Magtatambag, Gamhanang Diyos, Walay Kataposang Amahan, Prinsipe sa Pakigdait',
        'Hari sa Babilonia, Pangulo sa Tiro, Maghuhukom sa Moab, Taming sa Damasco',
        'Manalagna sa Sinai, Eskriba sa Juda, Magbalantay sa Efraim, Magbalantay sa Galaad',
        'Kapitan sa Karwahe, Agalon sa Bulawan, Magtutukod sa Paril, Magbalantay sa Ganghaan',
      ],
      correctAnswer: 'Katingalahang Magtatambag, Gamhanang Diyos, Walay Kataposang Amahan, Prinsipe sa Pakigdait',
      explanation: 'Ang Isaias 9:6 nagpahayag nga ang Iyang ngalan pagatawgon nga Katingalahang Magtatambag, Gamhanang Diyos, Walay Kataposang Amahan, Prinsipe sa Pakigdait.',
    },
  }),
  makeFull66SeedItem({
    questionCode: 'QB-66-ROM-001',
    category: 'Biblical Teachings',
    bibleBook: 'Romans',
    testament: 'New Testament',
    difficulty: 'Easy',
    scriptureReference: 'Romans 8:28',
    en: {
      questionText: 'According to Romans 8:28, for whom do all things work together for good?',
      options: [
        'For those who love God, for those who are called according to His purpose',
        'For those who hold high office in the Roman Senate',
        'For those who accumulate great earthly treasures',
        'For those who never face any trials or persecutions',
      ],
      correctAnswer: 'For those who love God, for those who are called according to His purpose',
      explanation: 'Romans 8:28 (WEB) states: "We know that all things work together for good for those who love God, for those who are called according to his purpose."',
    },
    tl: {
      questionText: 'Ayon sa Mga Taga-Roma 8:28, para kanino nagkakalakip-lakip ang lahat ng mga bagay sa ikabubuti?',
      options: [
        'Sa mga umiibig sa Diyos, sa mga tinawag ayon sa Kanyang layunin',
        'Sa mga may mataas na tungkulin sa Senado ng Roma',
        'Sa mga nagtitipon ng malaking kayamanan sa lupa',
        'Sa mga hindi kailanman dumaranas ng pagsubok',
      ],
      correctAnswer: 'Sa mga umiibig sa Diyos, sa mga tinawag ayon sa Kanyang layunin',
      explanation: 'Sinasabi sa Mga Taga-Roma 8:28 na ang lahat ng mga bagay ay nagkakalakip na gumagawa sa ikabubuti ng mga umiibig sa Diyos.',
    },
    ceb: {
      questionText: 'Sumala sa Mga Taga-Roma 8:28, alang kang kinsa ang tanang butang nagtinabangay alang sa kaayohan?',
      options: [
        'Alang kanila nga nahigugma sa Diyos, nga mga tinawag sumala sa Iyang katuyoan',
        'Alang niadtong adunay taas nga katungdanan sa Roma',
        'Alang niadtong nagtigom ug dakong bahandi sa yuta',
        'Alang niadtong wala gayod makasinati ug pagsulay',
      ],
      correctAnswer: 'Alang kanila nga nahigugma sa Diyos, nga mga tinawag sumala sa Iyang katuyoan',
      explanation: 'Ang Mga Taga-Roma 8:28 nag-ingon nga ang tanang butang nagtinabangay alang sa kaayohan niadtong nahigugma sa Diyos.',
    },
  }),
  makeFull66SeedItem({
    questionCode: 'QB-66-1CO-001',
    category: 'Biblical Teachings',
    bibleBook: '1 Corinthians',
    testament: 'New Testament',
    difficulty: 'Easy',
    scriptureReference: '1 Corinthians 13:13',
    en: {
      questionText: 'In 1 Corinthians 13:13, which three virtues remain, and which of them is the greatest?',
      options: [
        'Faith, hope, and love remain—and the greatest of these is love',
        'Wisdom,eloquence, and wealth remain—and the greatest is wisdom',
        'Prophecy, tongues, and knowledge remain—and the greatest is knowledge',
        'Strength, honor, and victory remain—and the greatest is victory',
      ],
      correctAnswer: 'Faith, hope, and love remain—and the greatest of these is love',
      explanation: '1 Corinthians 13:13 (WEB) states: "But now faith, hope, and love remain—these three. The greatest of these is love."',
    },
    tl: {
      questionText: 'Sa 1 Mga Taga-Corinto 13:13, anong tatlong bagay ang nananatili, at alin ang pinakadakila sa mga ito?',
      options: [
        'Pananampalataya, pag-asa, at pag-ibig—at ang pinakadakila sa mga ito ay ang pag-ibig',
        'Karunungan, kahusayang magsalita, at kayamanan—at ang pinakadakila ay karunungan',
        'Propesiya, iba\'t ibang wika, at kaalaman—at ang pinakadakila ay kaalaman',
        'Lakas, dangal, at tagumpay—at ang pinakadakila ay tagumpay',
      ],
      correctAnswer: 'Pananampalataya, pag-asa, at pag-ibig—at ang pinakadakila sa mga ito ay ang pag-ibig',
      explanation: 'Nakasaad sa 1 Mga Taga-Corinto 13:13: "Datapuwa\'t ngayo\'y nananatili ang tatlong ito: ang pananampalataya, ang pag-asa, at ang pag-ibig; ngunit ang pinakadakila sa mga ito ay ang pag-ibig."',
    },
    ceb: {
      questionText: 'Sa 1 Mga Taga-Corinto 13:13, unsang tulo ka hiyas ang nagpabilin, ug hain ang labing dako niini?',
      options: [
        'Pagtuo, paglaom, ug gugma—ug ang labing dako niini mao ang gugma',
        'Kaalam, kamaayong manulti, ug bahandi—ug ang labing dako mao ang kaalam',
        'Tagna, mga pinulongan, ug kahibalo—ug ang labing dako mao ang kahibalo',
        'Kusog, dungog, ug kadaogan—ug ang labing dako mao ang kadaogan',
      ],
      correctAnswer: 'Pagtuo, paglaom, ug gugma—ug ang labing dako niini mao ang gugma',
      explanation: 'Ang 1 Mga Taga-Corinto 13:13 nag-ingon nga nagpabilin ang pagtuo, paglaom, ug gugma, ug ang labing dako niini mao ang gugma.',
    },
  }),
  makeFull66SeedItem({
    questionCode: 'QB-66-GAL-001',
    category: 'Biblical Teachings',
    bibleBook: 'Galatians',
    testament: 'New Testament',
    difficulty: 'Easy',
    scriptureReference: 'Galatians 5:22-23',
    en: {
      questionText: 'Which nine qualities are listed together in Galatians 5:22-23 as the fruit of the Spirit?',
      options: [
        'Love, joy, peace, patience, kindness, goodness, faith, gentleness, and self-control',
        'Gold, silver, bronze, iron, cedar, olive oil, wheat, barley, and honey',
        'Ambition, debate, pride, wealth, authority, fame, conquest, decree, and tribute',
        'Fasting, pilgrimage, solitude, silence, sackcloth, ashes, trumpets, altars, and tithes',
      ],
      correctAnswer: 'Love, joy, peace, patience, kindness, goodness, faith, gentleness, and self-control',
      explanation: 'Galatians 5:22-23 (WEB) states: "But the fruit of the Spirit is love, joy, peace, patience, kindness, goodness, faith, gentleness, and self-control. Against such things there is no law."',
    },
    tl: {
      questionText: 'Anong siyam na katangian ang binanggit sa Mga Taga-Galacia 5:22-23 bilang bunga ng Espiritu?',
      options: [
        'Pag-ibig, kagalakan, kapayapaan, pagpapahinuhod, kabutihan, kabaitan, katapatan, kaamuan, at pagpipigil sa sarili',
        'Ginto, pilak, tanso, bakal, sedro, langis, trigo, sebada, at pulot',
        'Ambisyon, pakikipagtalo, kapalaluan, kayamanan, kapangyarihan, katanyagan, pananakop, utos, at buwis',
        'Pag-aayuno, paglalakbay, pag-iisa, katahimikan, sako, abo, pakakak, dambana, at ikapu',
      ],
      correctAnswer: 'Pag-ibig, kagalakan, kapayapaan, pagpapahinuhod, kabutihan, kabaitan, katapatan, kaamuan, at pagpipigil sa sarili',
      explanation: 'Sinasabi sa Mga Taga-Galacia 5:22-23 na ang bunga ng Espiritu ay pag-ibig, kagalakan, kapayapaan, pagpapahinuhod, kagandahang-loob, kabutihan, pagtatapat, kaamuan, at pagpipigil.',
    },
    ceb: {
      questionText: 'Unsang siyam ka hiyas ang gihisgotan sa Mga Taga-Galacia 5:22-23 ingon nga bunga sa Espiritu?',
      options: [
        'Gugma, kalipay, kalinaw, pailob, pagkamaayo, kaayo, pagtuo, kaaghop, ug pagpugong sa kaugalingon',
        'Bulawan, pilak, tumbaga, puthaw, sedro, lana, trigo, sebada, ug dugos',
        'Ambisyon, lantugi, garbo, bahandi, gahom, kabantog, kadaogan, sugo, ug buhis',
        'Pagpuasa, panaw, pag-inusara, kahilom, sako, abo, trumpeta, halaran, ug ikapulo',
      ],
      correctAnswer: 'Gugma, kalipay, kalinaw, pailob, pagkamaayo, kaayo, pagtuo, kaaghop, ug pagpugong sa kaugalingon',
      explanation: 'Ang Mga Taga-Galacia 5:22-23 nagtala sa bunga sa Espiritu: gugma, kalipay, kalinaw, pailob, pagkamaayo, kaayo, pagtuo, kaaghop, ug pagpugong sa kaugalingon.',
    },
  }),
  makeFull66SeedItem({
    questionCode: 'QB-66-PHP-001',
    category: 'Biblical Teachings',
    bibleBook: 'Philippians',
    testament: 'New Testament',
    difficulty: 'Easy',
    scriptureReference: 'Philippians 4:13',
    en: {
      questionText: 'What declaration of strength in Christ does the apostle Paul make in Philippians 4:13?',
      options: [
        'I can do all things through Christ, who strengthens me',
        'I can conquer Rome through the legions of Macedonia',
        'I have no need of prayer when I have earthly wealth',
        'I can escape prison through the gold of Philippi',
      ],
      correctAnswer: 'I can do all things through Christ, who strengthens me',
      explanation: 'Philippians 4:13 (WEB) declares: "I can do all things through Christ, who strengthens me."',
    },
    tl: {
      questionText: 'Anong pahayag ng kalakasan kay Kristo ang sinabi ni apostol Pablo sa Mga Taga-Filipos 4:13?',
      options: [
        'Lahat ng mga bagay ay aking magagawa sa pamamagitan ni Kristo na nagpapalakas sa akin',
        'Masasakop ko ang Roma sa pamamagitan ng hukbo ng Macedonia',
        'Hindi ko kailangan ng panalangin kapag may kayamanan sa lupa',
        'Makatatakas ako sa bilangguan sa pamamagitan ng ginto ng Filipos',
      ],
      correctAnswer: 'Lahat ng mga bagay ay aking magagawa sa pamamagitan ni Kristo na nagpapalakas sa akin',
      explanation: 'Sinasabi sa Mga Taga-Filipos 4:13: "Lahat ng mga bagay ay aking magagawa doon sa nagpapalakas sa akin."',
    },
    ceb: {
      questionText: 'Unsang pamahayag sa kalig-on diha kang Kristo ang gisulti ni Pablo sa Mga Taga-Filipos 4:13?',
      options: [
        'Mahimo nako ang tanang butang pinaagi kang Kristo nga nagpalig-on kanako',
        'Mapildi nako ang Roma pinaagi sa kasundalohan sa Macedonia',
        'Wala koy kinahanglan sa pag-ampo kon adunay bahandi sa yuta',
        'Makagawas ako sa bilanggoan pinaagi sa bulawan sa Filipos',
      ],
      correctAnswer: 'Mahimo nako ang tanang butang pinaagi kang Kristo nga nagpalig-on kanako',
      explanation: 'Ang Mga Taga-Filipos 4:13 nag-ingon: "Mahimo nako ang tanang butang pinaagi kaniya nga nagpalig-on kanako."',
    },
  }),
  makeFull66SeedItem({
    questionCode: 'QB-66-2TI-001',
    category: 'Biblical Knowledge',
    bibleBook: '2 Timothy',
    testament: 'New Testament',
    difficulty: 'Easy',
    scriptureReference: '2 Timothy 3:16',
    en: {
      questionText: 'According to 2 Timothy 3:16, what is true of every Scripture?',
      options: [
        'It is God-breathed and profitable for teaching, for reproof, for correction, and for instruction in righteousness',
        'It was written only for the priests of the first century',
        'It is profitable only when translated into Latin',
        'It should be read only once every seven years',
      ],
      correctAnswer: 'It is God-breathed and profitable for teaching, for reproof, for correction, and for instruction in righteousness',
      explanation: '2 Timothy 3:16 (WEB) states: "Every Scripture is God-breathed and profitable for teaching, for reproof, for correction, and for instruction in righteousness."',
    },
    tl: {
      questionText: 'Ayon sa 2 Timoteo 3:16, ano ang katotohanan tungkol sa lahat ng Kasulatan?',
      options: [
        'Kinasihan ng Diyos at mapapakinabangan sa pagtuturo, sa pagsansala, sa pagtutuwid, at sa pagsasanay sa katuwiran',
        'Isinulat lamang ito para sa mga saserdote noong unang siglo',
        'Mapapakinabangan lamang ito kapag isinalin sa Latin',
        'Dapat lamang itong basahin minsan tuwing pitong taon',
      ],
      correctAnswer: 'Kinasihan ng Diyos at mapapakinabangan sa pagtuturo, sa pagsansala, sa pagtutuwid, at sa pagsasanay sa katuwiran',
      explanation: 'Nakasaad sa 2 Timoteo 3:16 na ang lahat ng Kasulatan ay kinasihan ng Diyos at mapapakinabangan sa pagtuturo, pagsansala, pagtutuwid, at ikatututo sa katuwiran.',
    },
    ceb: {
      questionText: 'Sumala sa 2 Timoteo 3:16, unsa ang matuod mahitungod sa tibuok Kasulatan?',
      options: [
        'Kini ginhawa sa Diyos ug mapuslanon alang sa pagpanudlo, sa pagbadlong, sa pagtul-id, ug sa pagmatuto sa pagkamatarong',
        'Gisulat lamang kini alang sa mga pari sa unang siglo',
        'Mapuslanon lamang kini kon hubaron sa Latin',
        'Kinahanglan basahon lamang kini kausa matag pito ka tuig',
      ],
      correctAnswer: 'Kini ginhawa sa Diyos ug mapuslanon alang sa pagpanudlo, sa pagbadlong, sa pagtul-id, ug sa pagmatuto sa pagkamatarong',
      explanation: 'Ang 2 Timoteo 3:16 nag-ingon nga ang tibuok Kasulatan gidasig sa Diyos ug mapuslanon alang sa pagpanudlo, pagbadlong, pagtul-id, ug pagmatuto sa pagkamatarong.',
    },
  }),
];

